<!-- eslint-disable no-irregular-whitespace -->
<template>
  <div class="auth-wrapper">
    <div class="maincontainer">
      <div
        class="auth-container"
        :class="{ 'registration-active': isRegistered }"
      >
        <!-- COLONNA SINISTRA - Brand -->
        <div
          class="brand-panel"
          style="--login-background: url('/Pagina%20Login/Sfondo%20Login.png')"
        >
          <div class="brand-content">
            <img :src="logoUrl" alt="MyStudio" class="logo" />
            <h1>MyStudio</h1>
            <p class="tagline">Il tuo studio contabile, più intelligente</p>
            <router-link to="/" class="home-link">
              <img :src="logoUrl" alt="Home" class="logo-mini" />
              <span>Torna alla Home</span>
            </router-link>
          </div>
        </div>

        <!-- COLONNA DESTRA - Form -->
        <div class="form-panel">
          <div
            ref="formWrapper"
            class="form-wrapper"
            :class="{
              'centered-form': !isRegistered,
              'scrollable-form': isRegistered,
              'registration-choice-form': isRegistered && !registrationType,
              'registration-first-step':
                isRegistered &&
                registrationType &&
                !studioTrovato &&
                !privacyPhase &&
                !riepilogoFinale,
              'new-studio-registration-step':
                isRegistered &&
                registrationType === 'newStudio' &&
                !privacyPhase &&
                !riepilogoFinale,
              'existing-studio-registration-step':
                isRegistered &&
                registrationType === 'existingStudio' &&
                !studioTrovato &&
                !riepilogoFinale,
            }"
          >
            <!-- LOGIN -->
            <div v-if="!isRegistered" class="auth-form">
              <h2>Accedi a MyStudio</h2>
              <p class="form-subtitle">Inserisci le tue credenziali</p>
              <div v-if="justRegistered" class="alert success">
                Inserisci la password per per accedere.
              </div>
              <div v-if="errorMessage1" class="alert error">
                {{ errorMessage1 }}
              </div>
              <div
                v-if="
                  pendingActivation && !pendingActivationVerificationPending
                "
                class="verification-box"
              >
                <h4>
                  {{
                    pendingActivationClerkTicket
                      ? "Completa invito MyStudio"
                      : "Completa attivazione account"
                  }}
                </h4>
                <p v-if="pendingActivationClerkTicket">
                  Hai aperto un invito MyStudio. Scegli la password che vuoi
                  usare per completare l'account e accedere.
                </p>
                <p v-else>
                  Il tuo studio ha già creato l'utenza per
                  <strong>{{ email }}</strong
                  ><template v-if="pendingActivationStudioName">
                    nello studio {{ pendingActivationStudioName }}</template
                  >. Conferma la password che vuoi usare per accedere.
                </p>
                <div class="input-group">
                  <label>Codice fiscale</label>
                  <input
                    type="text"
                    v-model.trim="pendingActivationFiscalCode"
                    placeholder="RSSMRA80A01H501U"
                    maxlength="16"
                    required
                    class="input-field fiscal-code-input"
                    autocomplete="off"
                  />
                </div>
                <div class="input-group password-group">
                  <label>Password</label>
                  <div class="password-wrapper">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      v-model="password"
                      :placeholder="`Minimo ${minimumPasswordLength} caratteri`"
                      :minlength="minimumPasswordLength"
                      :maxlength="maximumPasswordLength"
                      required
                      class="input-field"
                      autocomplete="new-password"
                    />
                    <i
                      :class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
                      :title="
                        showPassword ? 'Nascondi password' : 'Mostra password'
                      "
                      @click="showPassword = !showPassword"
                      class="eye-icon"
                    ></i>
                  </div>
                  <ul v-if="password" class="password-requirements">
                    <li
                      v-for="check in passwordPolicyChecks"
                      :key="check.key"
                      :class="{ valid: check.valid }"
                    >
                      {{ check.valid ? "✓" : "•" }} {{ check.label }}
                    </li>
                  </ul>
                </div>
                <div class="input-group password-group">
                  <label>Conferma password</label>
                  <div class="password-wrapper">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      v-model="confirmPassword"
                      placeholder="Ripeti la password"
                      required
                      class="input-field"
                    />
                  </div>
                </div>
                <div class="actions">
                  <button
                    type="button"
                    class="btn-primary"
                    :disabled="isLoading"
                    @click="startPendingActivationVerification"
                  >
                    <span v-if="isLoading" class="spinner"></span>
                    {{
                      pendingActivationClerkTicket
                        ? "Completa e accedi"
                        : "Invia codice di verifica"
                    }}
                  </button>
                  <button
                    type="button"
                    class="btn-secondary"
                    :disabled="isLoading"
                    @click="cancelPendingActivation"
                  >
                    Annulla
                  </button>
                </div>
              </div>
              <div
                v-else-if="pendingActivationVerificationPending"
                class="verification-box"
              >
                <h4>Verifica la tua email</h4>
                <p>
                  Ti abbiamo inviato un codice a <strong>{{ email }}</strong
                  >. Inseriscilo qui per attivare l'account.
                </p>
                <div class="input-group">
                  <label>Codice di verifica</label>
                  <input
                    type="text"
                    v-model="pendingActivationCode"
                    placeholder="123456"
                    required
                    class="input-field"
                    autocomplete="one-time-code"
                  />
                </div>
                <div class="actions">
                  <button
                    type="button"
                    class="btn-primary"
                    :disabled="isLoading || !pendingActivationCode.trim()"
                    @click="verifyPendingActivationCode"
                  >
                    <span v-if="isLoading" class="spinner"></span>
                    Verifica e accedi
                  </button>
                  <button
                    type="button"
                    class="btn-secondary"
                    :disabled="isLoading"
                    @click="cancelPendingActivation"
                  >
                    Annulla
                  </button>
                </div>
              </div>
              <form
                v-else-if="forgotPasswordMode"
                class="verification-box"
                @submit.prevent="
                  forgotPasswordCodeSent
                    ? resetForgottenPassword()
                    : sendForgotPasswordCode()
                "
              >
                <h4>Reimposta password</h4>
                <p v-if="!forgotPasswordCodeSent">
                  Inserisci l'email del tuo account. Ti invieremo un codice per
                  scegliere una nuova password.
                </p>
                <p v-else>
                  Abbiamo inviato un codice a
                  <strong>{{ forgotPasswordEmail }}</strong
                  >. Inseriscilo qui e scegli la nuova password.
                </p>

                <div v-if="!forgotPasswordCodeSent" class="input-group">
                  <label>Email</label>
                  <input
                    type="email"
                    v-model="forgotPasswordEmail"
                    placeholder="nome@studio.it"
                    required
                    class="input-field"
                  />
                </div>

                <template v-else>
                  <div class="input-group">
                    <label>Codice di verifica</label>
                    <input
                      type="text"
                      v-model="forgotPasswordCode"
                      placeholder="123456"
                      required
                      class="input-field"
                      autocomplete="one-time-code"
                    />
                  </div>
                  <div class="input-group password-group">
                    <label>Nuova password</label>
                    <div class="password-wrapper">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        v-model="forgotPasswordNewPassword"
                        :placeholder="`Minimo ${minimumPasswordLength} caratteri`"
                        :minlength="minimumPasswordLength"
                        :maxlength="maximumPasswordLength"
                        required
                        class="input-field"
                        autocomplete="new-password"
                      />
                      <i
                        :class="
                          showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'
                        "
                        :title="
                          showPassword ? 'Nascondi password' : 'Mostra password'
                        "
                        @click="showPassword = !showPassword"
                        class="eye-icon"
                      ></i>
                    </div>
                    <ul
                      v-if="forgotPasswordNewPassword"
                      class="password-requirements"
                    >
                      <li
                        v-for="check in forgotPasswordPolicyChecks"
                        :key="check.key"
                        :class="{ valid: check.valid }"
                      >
                        {{ check.valid ? "✓" : "•" }} {{ check.label }}
                      </li>
                    </ul>
                  </div>
                  <div class="input-group password-group">
                    <label>Conferma password</label>
                    <div class="password-wrapper">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        v-model="forgotPasswordConfirmPassword"
                        placeholder="Ripeti la password"
                        required
                        class="input-field"
                        autocomplete="new-password"
                      />
                    </div>
                  </div>
                </template>

                <div class="actions">
                  <button
                    type="submit"
                    class="btn-primary"
                    :disabled="isLoading"
                  >
                    <span v-if="isLoading" class="spinner"></span>
                    {{
                      forgotPasswordCodeSent
                        ? "Aggiorna password e accedi"
                        : "Invia codice"
                    }}
                  </button>
                  <button
                    type="button"
                    class="btn-secondary"
                    :disabled="isLoading"
                    @click="cancelForgotPassword"
                  >
                    Annulla
                  </button>
                </div>
              </form>
              <form v-else @submit.prevent="loginUser">
                <div class="input-group">
                  <label>Email</label>
                  <div class="field-with-icon">
                    <i class="fa-regular fa-envelope" aria-hidden="true"></i>
                    <input
                      type="email"
                      v-model="email"
                      placeholder="nome@studio.it"
                      required
                      class="input-field has-leading-icon"
                    />
                  </div>
                </div>
                <div class="input-group password-group">
                  <div class="password-label-row">
                    <label>Password</label>
                    <button
                      v-if="clerkEnabled"
                      type="button"
                      class="forgot-password-link"
                      :disabled="isLoading || isGuestLoading"
                      @click="startForgotPassword"
                    >
                      Password dimenticata?
                    </button>
                  </div>
                  <div class="password-wrapper field-with-icon">
                    <i class="fa-solid fa-lock" aria-hidden="true"></i>
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      v-model="password"
                      placeholder="••••••••"
                      required
                      class="input-field has-leading-icon"
                    />
                    <i
                      :class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"
                      :title="
                        showPassword ? 'Nascondi password' : 'Mostra password'
                      "
                      @click="showPassword = !showPassword"
                      class="eye-icon"
                    ></i>
                  </div>
                </div>
                <button
                  type="submit"
                  class="btn-primary"
                  :disabled="isLoading || isGuestLoading"
                >
                  <span v-if="isLoading" class="spinner"></span>
                  <span>Accedi</span>
                  <i
                    v-if="!isLoading"
                    class="fa-solid fa-arrow-right"
                    aria-hidden="true"
                  ></i>
                </button>
              </form>
              <button
                v-if="
                  !pendingActivation &&
                  !pendingActivationVerificationPending &&
                  !forgotPasswordMode
                "
                type="button"
                class="btn-guest"
                :disabled="isLoading || isGuestLoading"
                @click="accessAsGuest"
              >
                <span
                  v-if="isGuestLoading"
                  class="spinner guest-spinner"
                ></span>
                <i v-else class="fa-regular fa-eye" aria-hidden="true"></i>
                {{
                  isGuestLoading ? "Accesso ospite..." : "Accedi come ospite"
                }}
              </button>
              <p
                v-if="
                  !pendingActivation &&
                  !pendingActivationVerificationPending &&
                  !forgotPasswordMode
                "
                class="switch-text"
              >
                Non hai un account?
                <button
                  type="button"
                  class="link-btn"
                  @click="toggleRegistration"
                >
                  Registrati ora
                </button>
              </p>
            </div>

            <!-- REGISTRAZIONE -->
            <div v-else class="auth-form auth-form-registration">
              <button
                v-if="showBackButton"
                type="button"
                class="back-btn"
                @click="goBack"
              >
                ← Indietro
              </button>

              <div
                class="form-header"
                :class="{
                  'choice-header': !registrationType,
                  'detail-header': registrationType,
                }"
              >
                <h2
                  :class="{
                    'title-large': !registrationType,
                    'title-small': registrationType,
                  }"
                >
                  Crea il tuo account MyStudio
                </h2>
                <p v-if="!registrationType" class="registration-subtitle">
                  Scegli come vuoi iniziare
                </p>
              </div>

              <!-- Selezione tipo registrazione -->
              <div v-if="!registrationType" class="type-selection">
                <button
                  type="button"
                  class="type-card"
                  @click="registrationType = 'newStudio'"
                  :class="{ selected: registrationType === 'newStudio' }"
                >
                  <div class="type-card-icon new-studio">
                    <i class="fa-solid fa-building"></i>
                  </div>
                  <h3 class="card-title">Nuovo Studio</h3>
                  <p>Registra il tuo studio contabile a MyStudio</p>
                  <span class="type-card-arrow" aria-hidden="true">
                    <i class="fa-solid fa-arrow-right"></i>
                  </span>
                </button>
                <button
                  type="button"
                  class="type-card"
                  @click="registrationType = 'existingStudio'"
                  :class="{ selected: registrationType === 'existingStudio' }"
                >
                  <div class="type-card-icon existing-studio">
                    <i class="fa-solid fa-user-plus"></i>
                  </div>
                  <h3 class="card-title">Nuovo Utente</h3>
                  <p>Registrati a uno studio già esistente</p>
                  <span class="type-card-arrow" aria-hidden="true">
                    <i class="fa-solid fa-arrow-right"></i>
                  </span>
                </button>
              </div>

              <!-- Form registrazione (dopo scelta tipo) -->
              <transition name="fade">
                <form
                  v-if="
                    registrationType &&
                    !studioTrovato &&
                    !privacyPhase &&
                    !riepilogoFinale
                  "
                  @submit.prevent="advanceRegistration"
                  class="registration-form"
                >
                  <div class="registration-stage-card">
                    <span class="stage-badge">{{
                      registrationStageLabel
                    }}</span>
                    <h3>{{ registrationStageTitle }}</h3>
                    <p>{{ registrationStageDescription }}</p>
                  </div>

                  <div v-if="errorMessage2" class="alert error">
                    {{ errorMessage2 }}
                  </div>
                  <div v-if="errorMessage3" class="alert error">
                    {{ errorMessage3 }}
                  </div>
                  <div v-if="successMessage" class="alert success">
                    {{ successMessage }}
                  </div>

                  <!-- Nome Studio -->
                  <div
                    v-if="
                      registrationType === 'existingStudio' ||
                      !newStudioAccountPhase
                    "
                    class="input-group studio-field"
                  >
                    <label>
                      {{
                        registrationType === "newStudio"
                          ? "Nome del nuovo Studio"
                          : "Nome dello Studio esistente"
                      }}
                    </label>
                    <input
                      type="text"
                      v-model="nomeStudio"
                      :placeholder="
                        registrationType === 'newStudio'
                          ? 'Studio Rossi & Associati'
                          : 'Cerca il tuo studio'
                      "
                      required
                      class="input-field"
                    />
                  </div>

                  <div
                    v-if="
                      registrationType === 'newStudio' && !newStudioAccountPhase
                    "
                    class="input-group"
                  >
                    <label>Partita IVA dello studio</label>
                    <input
                      type="text"
                      inputmode="numeric"
                      v-model.trim="partitaIva"
                      placeholder="01234567890"
                      pattern="[0-9]{11}"
                      maxlength="11"
                      required
                      class="input-field"
                    />
                  </div>

                  <div
                    v-if="
                      registrationType === 'newStudio' && !newStudioAccountPhase
                    "
                    class="input-group full-width-field"
                  >
                    <label>Indirizzo sede</label>
                    <input
                      type="text"
                      v-model.trim="indirizzoSede"
                      placeholder="Via Roma 10"
                      maxlength="255"
                      required
                      class="input-field"
                    />
                  </div>

                  <div
                    v-if="
                      registrationType === 'newStudio' && !newStudioAccountPhase
                    "
                    class="row registration-location-row"
                  >
                    <div class="input-group">
                      <label>CAP</label>
                      <input
                        type="text"
                        inputmode="numeric"
                        v-model.trim="cap"
                        placeholder="20100"
                        pattern="[0-9]{5}"
                        maxlength="5"
                        required
                        class="input-field"
                      />
                    </div>
                    <div class="input-group">
                      <label>Città</label>
                      <input
                        type="text"
                        v-model.trim="citta"
                        placeholder="Milano"
                        maxlength="100"
                        required
                        class="input-field"
                      />
                    </div>
                    <div class="input-group">
                      <label>Provincia</label>
                      <input
                        type="text"
                        v-model.trim="provincia"
                        placeholder="MI"
                        pattern="[A-Za-z]{2}"
                        maxlength="2"
                        required
                        class="input-field uppercase-input"
                      />
                    </div>
                  </div>

                  <!-- Chiave (solo per studio esistente) -->
                  <div
                    v-if="registrationType === 'existingStudio'"
                    class="input-group registration-key-field"
                  >
                    <label>
                      Chiave di registrazione
                      <span class="info-tooltip">
                        <i
                          class="fa-solid fa-circle-info"
                          aria-hidden="true"
                        ></i>
                        <span class="tooltip"
                          >Chiedi la chiave all'amministratore dello studio, la
                          può recuperare nell'Area Gestionale</span
                        >
                      </span>
                    </label>
                    <input
                      type="text"
                      v-model="registrationKey"
                      placeholder="es. ABC123XYZ"
                      required
                      class="input-field"
                    />
                  </div>

                  <!-- Email -->
                  <div
                    v-if="
                      registrationType === 'existingStudio' ||
                      newStudioAccountPhase
                    "
                    class="input-group email-field"
                  >
                    <label>
                      {{
                        registrationType === "newStudio"
                          ? "Email amministratore"
                          : "La tua email"
                      }}
                      <span
                        v-if="registrationType === 'newStudio'"
                        class="info-tooltip"
                      >
                        <i
                          class="fa-solid fa-circle-info"
                          aria-hidden="true"
                        ></i>
                        <span class="tooltip"
                          >Sarai il primo amministratore. Una volta registrato
                          potrai rendere altri utenti amministratori, se lo
                          desideri</span
                        >
                      </span>
                    </label>
                    <input
                      type="email"
                      v-model="email"
                      placeholder="utente@studio.it"
                      required
                      class="input-field"
                    />
                  </div>

                  <div
                    v-if="
                      registrationType === 'existingStudio' ||
                      newStudioAccountPhase
                    "
                    class="input-group fiscal-code-field"
                  >
                    <label>Codice fiscale personale</label>
                    <input
                      type="text"
                      v-model.trim="codiceFiscale"
                      placeholder="RSSMRA80A01H501U"
                      maxlength="16"
                      required
                      class="input-field fiscal-code-input"
                      autocomplete="off"
                    />
                  </div>

                  <div
                    v-if="
                      registrationType === 'existingStudio' ||
                      newStudioAccountPhase
                    "
                    class="row"
                  >
                    <div class="input-group">
                      <label>Nome</label>
                      <input
                        type="text"
                        v-model="firstName"
                        placeholder="Mario"
                        required
                        class="input-field"
                      />
                    </div>
                    <div class="input-group">
                      <label>Cognome</label>
                      <input
                        type="text"
                        v-model="lastName"
                        placeholder="Rossi"
                        required
                        class="input-field"
                      />
                    </div>
                  </div>

                  <!-- Password -->
                  <div
                    v-if="
                      (registrationType === 'existingStudio' ||
                        newStudioAccountPhase) &&
                      !(clerkEnabled && clerkSignUpComplete)
                    "
                    class="input-group password-group"
                  >
                    <label>Password</label>
                    <div class="password-wrapper">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        v-model="password"
                        :placeholder="`Minimo ${minimumPasswordLength} caratteri`"
                        :minlength="minimumPasswordLength"
                        :maxlength="maximumPasswordLength"
                        required
                        class="input-field"
                        autocomplete="new-password"
                      />
                      <i
                        :class="
                          showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'
                        "
                        :title="
                          showPassword ? 'Nascondi password' : 'Mostra password'
                        "
                        @click="showPassword = !showPassword"
                        class="eye-icon"
                      ></i>
                    </div>
                    <ul v-if="password" class="password-requirements">
                      <li
                        v-for="check in passwordPolicyChecks"
                        :key="check.key"
                        :class="{ valid: check.valid }"
                      >
                        {{ check.valid ? "✓" : "•" }} {{ check.label }}
                      </li>
                    </ul>
                    <div v-if="showStrengthBar" class="strength-meter">
                      <div class="bar">
                        <div class="fill" :class="strengthLevel"></div>
                      </div>
                      <span class="label">{{ strengthLabel }}</span>
                    </div>
                  </div>

                  <!-- Conferma Password -->
                  <div
                    v-if="
                      (registrationType === 'existingStudio' ||
                        newStudioAccountPhase) &&
                      !(clerkEnabled && clerkSignUpComplete)
                    "
                    class="input-group password-group"
                  >
                    <label>Conferma Password</label>
                    <div class="password-wrapper">
                      <input
                        :type="showPassword ? 'text' : 'password'"
                        v-model="confirmPassword"
                        placeholder="Ripeti la password"
                        required
                        class="input-field"
                      />
                    </div>
                    <div v-if="passwordMismatch" class="field-error">
                      Le password non coincidono
                    </div>
                  </div>

                  <button
                    type="submit"
                    class="btn-primary"
                    :disabled="
                      isLoading ||
                      isRegistratiDisabled ||
                      isCurrentRegistrationStepInvalid
                    "
                  >
                    <span v-if="isLoading" class="spinner"></span>
                    <span v-else>Continua</span>
                    <i
                      v-if="!isLoading"
                      class="fa-solid fa-arrow-right"
                      aria-hidden="true"
                    ></i>
                  </button>
                  <div v-if="errorMessage4" class="alert error">
                    {{ errorMessage4 }}
                  </div>
                </form>
              </transition>

              <!-- SEZIONE INTERMEDIA PER STUDIO TROVATO -->
              <div
                v-if="
                  studioTrovato &&
                  registrationType === 'existingStudio' &&
                  !riepilogoFinale
                "
                class="summary-box summary-box-polished legal-consent-box"
              >
                <div class="summary-topline">Passo 2 di 3</div>
                <h3>Studio trovato</h3>
                <div class="review-grid">
                  <div class="review-item">
                    <span class="review-label">Studio</span>
                    <strong>{{ nomeStudioTrovato }}</strong>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Account</span>
                    <strong>{{ email }}</strong>
                  </div>
                </div>

                <div
                  class="privacy-content simplified legal-summary legal-panel"
                >
                  <p>
                    Prima di completare la registrazione, prendi visione dei
                    documenti relativi all'utilizzo della piattaforma.
                  </p>
                  <p class="privacy-p">
                    <a
                      href="/Pagina%20Welcome/MyStudio%20-%20Informativa%20Privacy.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="privacy-link-btn prominent legal-link-card"
                    >
                      Leggi l'Informativa Privacy completa
                    </a>
                  </p>
                  <p class="privacy-p">
                    <a
                      href="/Pagina%20Welcome/MyStudio%20-%20Termini%20e%20condizioni%20di%20utilizzo.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="privacy-link-btn prominent legal-link-card"
                    >
                      Leggi i Termini e condizioni di utilizzo
                    </a>
                  </p>
                  <p>
                    L'utilizzo della piattaforma è disciplinato anche dai
                    Termini e condizioni di utilizzo, che descrivono regole del
                    servizio, responsabilità e condizioni contrattuali
                    applicabili al tuo account.
                  </p>
                </div>

                <div class="privacy-accept legal-checks">
                  <label>
                    <input
                      type="checkbox"
                      v-model="privacyAccettata"
                      required
                    />
                    Dichiaro di aver letto l'informativa privacy
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      v-model="terminiAccettati"
                      required
                    />
                    Accetto i Termini e condizioni di utilizzo della piattaforma
                  </label>
                </div>

                <button
                  v-if="!successMessage"
                  class="btn-primary confirmbutton"
                  :disabled="
                    isLoading ||
                    isRegistratiDisabled ||
                    isFormInvalid ||
                    !privacyAccettata ||
                    !terminiAccettati
                  "
                  @click="goToRiepilogoFromPrivacy"
                >
                  <span v-if="isLoading" class="spinner"></span>
                  Procedi al riepilogo
                </button>

                <div v-if="errorMessage4" class="alert error">
                  {{ errorMessage4 }}
                </div>

                <div v-if="successMessage">
                  <div
                    class="success-box polished-success-box registration-success-panel"
                  >
                    <h4>{{ successMessage }}</h4>
                    <p>
                      {{
                        clerkEnabled
                          ? "Account MyStudio collegato correttamente."
                          : "Puoi accedere a MyStudio con le credenziali appena create."
                      }}
                    </p>
                    <button class="btn-primary" @click="goToLogin">
                      Vai al Login
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-if="
                  registrationType === 'newStudio' &&
                  privacyPhase &&
                  !riepilogoFinale
                "
                class="summary-box summary-box-polished privacy-box legal-consent-box"
              >
                <div class="summary-topline">Passo 3 di 4</div>
                <h3>Privacy e condizioni di utilizzo</h3>

                <div class="privacy-content simplified legal-panel">
                  <p>
                    I tuoi dati personali saranno trattati da
                    <strong>MyStudio SRL</strong>, in qualità di Titolare del
                    trattamento, per le finalità di creazione e gestione del tuo
                    account MyStudio, adempimenti contrattuali, amministrativi,
                    contabili e fiscali.
                  </p>
                  <p>
                    Il trattamento si basa sull'esecuzione del contratto e su
                    obblighi di legge (GDPR art. 6). I dati saranno conservati
                    per la durata del rapporto e per i termini previsti dalla
                    legge (normalmente 10 anni per obblighi fiscali).
                  </p>
                  <p>
                    Hai diritto di accesso, rettifica, cancellazione,
                    limitazione, opposizione e portabilità dei dati, nonché di
                    proporre reclamo al Garante per la protezione dei dati
                    personali.
                  </p>
                  <p>
                    Per tutte le informazioni dettagliate (compresi i
                    destinatari dei dati, le modalità di esercizio dei diritti,
                    eventuali trasferimenti extra-UE) leggi l'informativa
                    completa:
                  </p>
                  <p class="privacy-p">
                    <a
                      href="/Pagina%20Welcome/MyStudio%20-%20Informativa%20Privacy.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="privacy-link-btn prominent legal-link-card"
                    >
                      Leggi l'Informativa Privacy completa
                    </a>
                  </p>
                  <p>
                    L'utilizzo della piattaforma è disciplinato anche dai
                    Termini e condizioni di utilizzo, che descrivono regole del
                    servizio, responsabilità e condizioni contrattuali
                    applicabili al tuo account.
                  </p>
                  <p class="privacy-p">
                    <a
                      href="/Pagina%20Welcome/MyStudio%20-%20Termini%20e%20condizioni%20di%20utilizzo.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="privacy-link-btn prominent legal-link-card"
                    >
                      Leggi i Termini e condizioni di utilizzo
                    </a>
                  </p>
                </div>

                <div class="privacy-accept legal-checks">
                  <label>
                    <input
                      type="checkbox"
                      v-model="privacyAccettata"
                      required
                    />
                    Dichiaro di aver letto e compreso l’informativa privacy
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      v-model="terminiAccettati"
                      required
                    />
                    Accetto i Termini e condizioni di utilizzo della piattaforma
                  </label>
                </div>

                <div class="actions">
                  <button
                    class="btn-primary"
                    :disabled="
                      !privacyAccettata || !terminiAccettati || isLoading
                    "
                    @click="goToRiepilogoFromPrivacy"
                  >
                    <span v-if="isLoading" class="spinner"></span>
                    Procedi al riepilogo
                  </button>
                  <button class="btn-secondary" @click="privacyPhase = false">
                    Indietro
                  </button>
                </div>

                <div v-if="errorMessage4" class="alert error mt-3">
                  {{ errorMessage4 }}
                </div>
              </div>

              <!-- Riepilogo finale -->
              <div
                v-if="riepilogoFinale"
                class="summary-box summary-box-polished final-review-box"
              >
                <div class="summary-topline">
                  {{
                    registrationType === "newStudio"
                      ? "Passo 4 di 4"
                      : "Passo 3 di 3"
                  }}
                </div>
                <h3>Riepilogo Registrazione</h3>
                <div class="review-grid">
                  <div class="review-item">
                    <span class="review-label">Studio</span>
                    <strong>{{
                      registrationType === "newStudio"
                        ? nomeStudio
                        : nomeStudioTrovato || nomeStudio
                    }}</strong>
                  </div>
                  <div class="review-item">
                    <span class="review-label">{{
                      registrationType === "newStudio"
                        ? "Email amministratore"
                        : "Email account"
                    }}</span>
                    <strong>{{ email }}</strong>
                  </div>
                  <div class="review-item review-item-wide">
                    <span class="review-label">Profilo utente</span>
                    <strong>{{
                      [firstName, lastName].filter(Boolean).join(" ") || email
                    }}</strong>
                  </div>
                  <div class="review-item">
                    <span class="review-label">Codice fiscale</span>
                    <strong>{{ codiceFiscale.toUpperCase() }}</strong>
                  </div>
                  <template v-if="registrationType === 'newStudio'">
                    <div class="review-item">
                      <span class="review-label">Partita IVA</span>
                      <strong>{{ partitaIva }}</strong>
                    </div>
                    <div class="review-item review-item-wide">
                      <span class="review-label">Sede</span>
                      <strong>
                        {{ indirizzoSede }}, {{ cap }} {{ citta }} ({{
                          provincia.toUpperCase()
                        }})
                      </strong>
                    </div>
                  </template>
                </div>

                <div
                  v-if="clerkEnabled && clerkVerificationPending"
                  class="verification-box"
                >
                  <h4>Verifica la tua email</h4>
                  <p v-if="clerkVerificationCodeSent">
                    Ti abbiamo inviato un codice a <strong>{{ email }}</strong
                    >. Inseriscilo qui per completare la registrazione.
                  </p>
                  <p v-else>
                    Se hai già ricevuto un codice puoi inserirlo qui. In caso
                    contrario, attendi il termine indicato e richiedi un nuovo
                    codice.
                  </p>
                  <div
                    v-if="clerkVerificationNotice"
                    class="alert success verification-notice"
                  >
                    {{ clerkVerificationNotice }}
                  </div>
                  <div class="input-group">
                    <label>Codice di verifica</label>
                    <input
                      type="text"
                      v-model="clerkVerificationCode"
                      placeholder="123456"
                      required
                      class="input-field"
                      autocomplete="one-time-code"
                    />
                  </div>
                  <div class="actions">
                    <button
                      type="button"
                      class="btn-primary"
                      :disabled="isLoading || !clerkVerificationCode.trim()"
                      @click="verifyClerkRegistrationCode"
                    >
                      <span v-if="isLoading" class="spinner"></span>
                      Verifica e completa
                    </button>
                    <div class="verification-secondary-actions">
                      <button
                        type="button"
                        class="btn-secondary"
                        :disabled="isLoading || clerkVerificationCooldownActive"
                        @click="resendClerkRegistrationCode"
                      >
                        {{ clerkVerificationResendLabel }}
                      </button>
                      <button
                        type="button"
                        class="btn-secondary"
                        :disabled="isLoading"
                        @click="editClerkRegistrationData"
                      >
                        Modifica dati
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!successMessage && !clerkVerificationPending"
                  class="actions"
                >
                  <button
                    type="button"
                    class="btn-primary"
                    :disabled="
                      isLoading ||
                      isRegistratiDisabled ||
                      isFormInvalid ||
                      clerkVerificationCooldownActive
                    "
                    @click="finalizeRegistration"
                  >
                    <span v-if="isLoading" class="spinner"></span>
                    {{
                      clerkEnabled
                        ? clerkVerificationSendLabel
                        : "Conferma e Registrati"
                    }}
                  </button>
                  <button
                    type="button"
                    @click="riepilogoFinale = false"
                    class="btn-secondary"
                  >
                    Indietro
                  </button>
                </div>

                <div v-if="errorMessage4" class="alert error">
                  {{ errorMessage4 }}
                </div>

                <div v-if="successMessage">
                  <div
                    class="success-box polished-success-box registration-success-panel"
                  >
                    <h4>{{ successMessage }}</h4>
                    <p>
                      Puoi accedere a MyStudio con le credenziali appena create.
                    </p>
                    <button class="btn-primary" @click="goToLogin">
                      Vai al Login
                    </button>
                  </div>
                </div>
              </div>

              <!-- Link torna al login -->
              <p v-if="!successMessage" class="switch-text">
                Hai già un account?
                <button
                  type="button"
                  class="link-btn"
                  @click="toggleRegistration"
                >
                  Accedi
                </button>
              </p>
            </div>
            <div class="scroll-spacer"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import {
  getClerk,
  getClerkProfile,
  getClerkToken,
  isClerkConfigured,
  signOutFromClerk,
} from "@/auth/clerk.js";
import {
  DEFAULT_CLERK_VERIFICATION_COOLDOWN_SECONDS,
  getClerkErrorMessage,
  getClerkRetryAfterSeconds,
  isClerkRateLimitError,
} from "@/auth/clerkRateLimit.js";
import {
  getClerkPasswordPolicy,
  getPasswordPolicyChecks,
  getPasswordPolicyError,
  isPasswordPolicySatisfied,
  normalizeClerkPasswordPolicy,
} from "@/auth/clerkPasswordPolicy.js";
import { disableGuestMode, enableGuestMode } from "@/utils/guestMode.js";

export default {
  data() {
    return {
      // Dati base
      nomeStudio: "",
      nomeStudioTrovato: "",
      registrationKey: "",
      selectedStudioId: "",
      existingStudioId: null,
      studioTrovato: false,
      newStudioAccountPhase: false,
      email: "",
      firstName: "",
      lastName: "",
      codiceFiscale: "",
      partitaIva: "",
      indirizzoSede: "",
      cap: "",
      citta: "",
      provincia: "",
      password: "",
      confirmPassword: "",
      conversioniDisponibili: 0,
      privacyAccettata: false,
      terminiAccettati: false,
      privacyPhase: false,
      // Modalità
      isRegistered: false,
      justRegistered: false,
      riepilogoFinale: false,
      registrationType: "",
      showPassword: false,

      // Messaggi
      errorMessage1: "",
      errorMessage2: "",
      errorMessage3: "",
      errorMessage4: "",
      successMessage: "",

      isLoading: false,
      isGuestLoading: false,
      isRegistratiDisabled: false,
      authNavigationInProgress: false,
      clerkEnabled: isClerkConfigured,
      passwordPolicy: normalizeClerkPasswordPolicy({
        min_length: isClerkConfigured ? 12 : 8,
      }),
      clerkLoading: false,
      clerkSignedIn: false,
      clerkBridgeInProgress: false,
      clerkVerificationPending: false,
      clerkVerificationCode: "",
      clerkVerificationCodeSent: false,
      clerkVerificationNotice: "",
      clerkVerificationCooldownUntil: 0,
      clerkVerificationCooldownRemaining: 0,
      clerkVerificationCooldownTimer: null,
      clerkSignUpComplete: false,
      pendingActivation: false,
      pendingActivationVerificationPending: false,
      pendingActivationCode: "",
      pendingActivationFirstName: "",
      pendingActivationLastName: "",
      pendingActivationFiscalCode: "",
      pendingActivationInviteToken: "",
      pendingActivationClerkTicket: "",
      pendingActivationStudioName: "",
      forgotPasswordMode: false,
      forgotPasswordCodeSent: false,
      forgotPasswordEmail: "",
      forgotPasswordCode: "",
      forgotPasswordNewPassword: "",
      forgotPasswordConfirmPassword: "",
      clerkUnsubscribe: null,

      logoUrl: "/Pagina%20Welcome/logo.png",
    };
  },
  watch: {
    isRegistered(newVal) {
      if (!newVal && this.justRegistered) {
        // Nasconde il messaggio dopo 8 secondi (o il tempo che preferisci)
        setTimeout(() => {
          this.justRegistered = false;
        }, 8000);
      }
    },
    registrationType(newVal) {
      this.newStudioAccountPhase = false;
      if (newVal) this.resetRegistrationScroll();
    },
    privacyPhase() {
      this.resetRegistrationScroll();
    },
    riepilogoFinale() {
      this.resetRegistrationScroll();
    },
    studioTrovato() {
      this.resetRegistrationScroll();
    },
  },
  computed: {
    registrationStageLabel() {
      if (this.registrationType === "newStudio") {
        return this.newStudioAccountPhase ? "Passo 2 di 4" : "Passo 1 di 4";
      }
      return "Passo 1 di 3";
    },
    registrationStageTitle() {
      if (this.registrationType === "newStudio") {
        return this.newStudioAccountPhase
          ? "Account amministratore"
          : "Dati dello studio";
      }
      return "Unisciti al tuo studio";
    },
    registrationStageDescription() {
      if (this.registrationType === "newStudio") {
        return this.newStudioAccountPhase
          ? "Inserisci i dati personali e scegli le credenziali di accesso."
          : "Inserisci i dati fiscali e l'indirizzo della sede.";
      }
      return "Inserisci la chiave dello studio e crea il tuo account personale.";
    },
    isCurrentRegistrationStepInvalid() {
      if (
        this.registrationType === "newStudio" &&
        !this.newStudioAccountPhase
      ) {
        return false;
      }
      return this.isFormInvalid;
    },
    canProceedToRiepilogo() {
      return (
        this.privacyAccettata && this.terminiAccettati && !this.isFormInvalid
      );
    },
    clerkVerificationCooldownActive() {
      return this.clerkVerificationCooldownRemaining > 0;
    },
    clerkVerificationSendLabel() {
      if (this.clerkVerificationCooldownActive) {
        return `Riprova tra ${this.clerkVerificationCooldownRemaining}s`;
      }
      return "Invia codice di verifica";
    },
    clerkVerificationResendLabel() {
      if (this.clerkVerificationCooldownActive) {
        return `Nuovo codice tra ${this.clerkVerificationCooldownRemaining}s`;
      }
      return "Invia nuovo codice";
    },
    showBackButton() {
      if (this.successMessage) return false;

      return (
        this.registrationType ||
        this.studioTrovato ||
        this.privacyPhase ||
        this.riepilogoFinale
      );
    },
    minimumPasswordLength() {
      return this.passwordPolicy.minLength;
    },
    maximumPasswordLength() {
      return this.passwordPolicy.maxLength
        ? this.passwordPolicy.maxLength - 1
        : null;
    },
    passwordPolicyChecks() {
      return getPasswordPolicyChecks(this.password, this.passwordPolicy);
    },
    forgotPasswordPolicyChecks() {
      return getPasswordPolicyChecks(
        this.forgotPasswordNewPassword,
        this.passwordPolicy
      );
    },
    passwordMeetsPolicy() {
      return isPasswordPolicySatisfied(this.password, this.passwordPolicy);
    },
    isTooShort() {
      if (this.clerkEnabled && this.clerkSignUpComplete) return false;
      return (
        this.password.length > 0 &&
        this.password.length < this.minimumPasswordLength
      );
    },
    showStrengthBar() {
      if (this.clerkEnabled && this.clerkSignUpComplete) return false;
      return this.password.length >= this.minimumPasswordLength;
    },
    strengthScore() {
      const pwd = this.password;
      if (!pwd || pwd.length < this.minimumPasswordLength) return 1;
      let score = 1;
      if (/[A-Z]/.test(pwd)) score++;
      if (/\d/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;
      if (
        pwd.length >= Math.max(10, this.minimumPasswordLength) &&
        /[A-Z]/.test(pwd) &&
        /\d/.test(pwd) &&
        /[^A-Za-z0-9]/.test(pwd)
      )
        score++;
      return Math.min(score, 4);
    },
    strengthLabel() {
      return (
        ["", "Debole", "Media", "Buona", "Ottima"][this.strengthScore] || ""
      );
    },
    strengthLevel() {
      return ["", "weak", "medium", "strong", "very-strong"][
        this.strengthScore
      ];
    },
    passwordMismatch() {
      if (this.clerkEnabled && this.clerkSignUpComplete) return false;
      return (
        this.password &&
        this.confirmPassword &&
        this.password !== this.confirmPassword
      );
    },
    isFormInvalid() {
      if (this.clerkEnabled && this.clerkSignUpComplete) return false;
      return !this.passwordMeetsPolicy || this.passwordMismatch;
    },
  },
  created() {
    if (this.$route.query.registrati === "1") {
      this.isRegistered = true;
    }
  },
  mounted() {
    if (this.clerkEnabled) {
      this.initClerkAuth();
    }
    if (this.$route.query.__clerk_ticket) {
      this.loadClerkInvitationFromTicket(this.$route.query.__clerk_ticket);
      return;
    }
    if (this.$route.query.invite) {
      this.loadInvitationFromToken(this.$route.query.invite);
    }
  },
  beforeDestroy() {
    if (typeof this.clerkUnsubscribe === "function") {
      this.clerkUnsubscribe();
    }
    this.stopClerkVerificationCooldownTimer();
  },

  methods: {
    resetRegistrationScroll() {
      this.$nextTick(() => {
        const wrapper = this.$refs.formWrapper;
        if (wrapper) wrapper.scrollTop = 0;
      });
    },
    toggleRegistration() {
      this.clearAllMessages();
      this.isRegistered = !this.isRegistered;
      this.resetFormFields();
    },
    goToLogin() {
      this.justRegistered = true; // attiva il messaggio di successo nel login
      this.clearAllMessages(); // pulisce errori e successMessage

      // Resetta SOLO i campi della registrazione (NON tocca email!)
      this.nomeStudio = "";
      this.nomeStudioTrovato = "";
      this.registrationKey = "";
      this.existingStudioId = null;
      this.firstName = "";
      this.lastName = "";
      this.codiceFiscale = "";
      this.partitaIva = "";
      this.indirizzoSede = "";
      this.cap = "";
      this.citta = "";
      this.provincia = "";
      this.password = ""; // password vuota (importante!)
      this.confirmPassword = "";
      this.privacyAccettata = false;
      this.terminiAccettati = false;
      this.privacyPhase = false;
      this.riepilogoFinale = false;
      this.registrationType = "";
      this.studioTrovato = false;
      this.newStudioAccountPhase = false;
      this.clerkVerificationPending = false;
      this.clerkVerificationCode = "";
      this.clerkVerificationCodeSent = false;
      this.clerkVerificationNotice = "";
      this.clerkSignUpComplete = false;
      this.pendingActivation = false;
      this.pendingActivationVerificationPending = false;
      this.pendingActivationCode = "";
      this.pendingActivationFirstName = "";
      this.pendingActivationLastName = "";
      this.pendingActivationFiscalCode = "";
      this.pendingActivationInviteToken = "";
      this.pendingActivationClerkTicket = "";
      this.pendingActivationStudioName = "";
      this.resetForgotPasswordState();

      // Passa alla vista login
      this.isRegistered = false;
    },

    goBack() {
      if (this.successMessage) {
        this.goToLogin();
        return;
      }

      if (this.riepilogoFinale) {
        this.riepilogoFinale = false;
      } else if (this.privacyPhase) {
        this.privacyPhase = false;
      } else if (this.studioTrovato) {
        this.studioTrovato = false;
      } else if (
        this.registrationType === "newStudio" &&
        this.newStudioAccountPhase
      ) {
        this.newStudioAccountPhase = false;
        this.resetRegistrationScroll();
      } else {
        this.registrationType = "";
      }
      this.clearAllMessages();
    },

    clearAllMessages() {
      this.errorMessage1 =
        this.errorMessage2 =
        this.errorMessage3 =
        this.errorMessage4 =
        this.successMessage =
          "";
    },

    resetFormFields() {
      this.nomeStudio = "";
      this.nomeStudioTrovato = "";
      this.registrationKey = "";
      this.existingStudioId = null;
      this.email = "";
      this.firstName = "";
      this.lastName = "";
      this.codiceFiscale = "";
      this.partitaIva = "";
      this.indirizzoSede = "";
      this.cap = "";
      this.citta = "";
      this.provincia = "";
      this.password = "";
      this.confirmPassword = "";
      this.privacyAccettata = false;
      this.terminiAccettati = false;
      this.privacyPhase = false;
      this.riepilogoFinale = false;
      this.registrationType = "";
      this.studioTrovato = false;
      this.newStudioAccountPhase = false;
      this.clerkVerificationPending = false;
      this.clerkVerificationCode = "";
      this.clerkVerificationCodeSent = false;
      this.clerkVerificationNotice = "";
      this.clerkSignUpComplete = false;
      this.pendingActivation = false;
      this.pendingActivationVerificationPending = false;
      this.pendingActivationCode = "";
      this.pendingActivationFirstName = "";
      this.pendingActivationLastName = "";
      this.pendingActivationFiscalCode = "";
      this.pendingActivationInviteToken = "";
      this.pendingActivationClerkTicket = "";
      this.pendingActivationStudioName = "";
      this.resetForgotPasswordState();
    },

    applyAuthSession(response, emailFallback = "") {
      const email = response.data.user_email || emailFallback || this.email;
      const userData = {
        email,
        first_name: response.data.first_name || "",
        last_name: response.data.last_name || "",
        is_admin: response.data.is_admin || false,
        is_admin_MyStudio: response.data.is_admin_MyStudio || false,
        studio: {
          nome: response.data.studio_nome || "Studio",
          piano: response.data.piano || "base",
        },
      };

      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("userEmail", email);
      localStorage.setItem(
        "isAdminMyStudio",
        JSON.stringify(response.data.is_admin_MyStudio)
      );
      localStorage.setItem("isAdmin", JSON.stringify(response.data.is_admin));
      localStorage.setItem("firstName", response.data.first_name || "");
      localStorage.setItem("lastName", response.data.last_name || "");
    },

    async initClerkAuth() {
      this.clerkLoading = true;

      try {
        const clerk = await getClerk();
        this.passwordPolicy = getClerkPasswordPolicy(clerk);

        if (typeof clerk.addListener === "function") {
          this.clerkUnsubscribe = clerk.addListener(({ session }) => {
            this.clerkSignedIn = Boolean(session);
          });
        }

        this.clerkSignedIn = Boolean(clerk.isSignedIn);
      } catch (error) {
        console.error("Errore inizializzazione Clerk", error);
        this.errorMessage1 =
          "Accesso sicuro non disponibile. Controlla la configurazione Clerk.";
      } finally {
        this.clerkLoading = false;
      }
    },

    async getClerkAuthHeaders() {
      const token = await getClerkToken();
      if (!token) {
        throw new Error("Sessione Clerk non disponibile.");
      }

      return {
        Authorization: `Bearer ${token}`,
      };
    },

    async activateClerkSession(clerk, sessionId) {
      sessionStorage.setItem("mystudioClerkBridgePending", "1");
      await clerk.setActive({
        session: sessionId,
        navigate: async () => undefined,
      });
      this.clerkSignedIn = true;
    },

    async navigateToHomeAfterAuth() {
      this.authNavigationInProgress = true;
      sessionStorage.removeItem("mystudioClerkBridgePending");

      try {
        await this.$router.push("/home");
      } catch (error) {
        this.authNavigationInProgress = false;
        throw error;
      }
    },

    getRawClerkErrorMessage(error) {
      return getClerkErrorMessage(error);
    },

    translateClerkErrorMessage(message) {
      if (!message) return "";

      const normalized = message.toLowerCase().trim();
      const exactTranslations = {
        "password is incorrect. try again, or use another method.":
          "La password non è corretta. Riprova oppure usa un altro metodo.",
        "couldn't find your account.": "Impossibile trovare il tuo account.",
        "you're already signed in.": "Hai già effettuato l'accesso.",
        "invalid verification code": "Codice di verifica non valido.",
        "verification code is invalid": "Il codice di verifica non è valido.",
        "verification code has expired": "Il codice di verifica è scaduto.",
        "identifier is invalid": "L'identificativo non è valido.",
        "password has been found in a data breach":
          "La password è stata trovata in una violazione di dati.",
        "password is too weak": "La password è troppo debole.",
      };

      if (exactTranslations[normalized]) {
        return exactTranslations[normalized];
      }

      if (normalized.includes("password is incorrect")) {
        return "La password non è corretta. Riprova oppure usa un altro metodo.";
      }
      if (normalized.includes("couldn't find your account")) {
        return "Impossibile trovare il tuo account.";
      }
      if (normalized.includes("already signed in")) {
        return "Hai già effettuato l'accesso.";
      }
      if (
        normalized.includes("invalid verification code") ||
        normalized.includes("verification code is invalid") ||
        normalized.includes("code is invalid")
      ) {
        return "Codice di verifica non valido.";
      }
      if (
        normalized.includes("verification code has expired") ||
        normalized.includes("code has expired")
      ) {
        return "Il codice di verifica è scaduto.";
      }
      if (normalized.includes("found in a data breach")) {
        return "La password è stata trovata in una violazione di dati.";
      }
      if (normalized.includes("password is too weak")) {
        return "La password è troppo debole.";
      }
      if (normalized.includes("given password is not strong enough")) {
        return "La password non è abbastanza robusta.";
      }
      if (normalized.includes("must contain at least one lowercase")) {
        return "La password deve contenere almeno una lettera minuscola.";
      }
      if (normalized.includes("must contain at least one uppercase")) {
        return "La password deve contenere almeno una lettera maiuscola.";
      }
      if (normalized.includes("must contain at least one number")) {
        return "La password deve contenere almeno un numero.";
      }
      if (normalized.includes("must contain at least one of the following")) {
        return "La password deve contenere almeno un carattere speciale consentito.";
      }
      if (
        normalized.includes("rate exceeded") ||
        normalized.includes("too many verification") ||
        normalized.includes("too many requests")
      ) {
        return "Sono stati richiesti troppi codici. Attendi qualche secondo prima di riprovare.";
      }
      if (
        (normalized.includes("password must be") ||
          normalized.includes("passwords must be")) &&
        normalized.includes("characters")
      ) {
        const requiredLength = message.match(/\d+/)?.[0];
        if (requiredLength) {
          return `La password deve contenere almeno ${requiredLength} caratteri.`;
        }
      }

      return message;
    },

    formatClerkError(error, fallback) {
      const clerkMessage = this.getRawClerkErrorMessage(error);
      const translatedMessage = this.translateClerkErrorMessage(clerkMessage);

      return translatedMessage || fallback;
    },

    getClerkVerificationCooldownStorageKey() {
      return "mystudioClerkVerificationCooldown";
    },

    stopClerkVerificationCooldownTimer() {
      if (this.clerkVerificationCooldownTimer) {
        clearInterval(this.clerkVerificationCooldownTimer);
        this.clerkVerificationCooldownTimer = null;
      }
    },

    updateClerkVerificationCooldown() {
      const remaining = Math.max(
        0,
        Math.ceil((this.clerkVerificationCooldownUntil - Date.now()) / 1000)
      );
      this.clerkVerificationCooldownRemaining = remaining;

      if (!remaining) {
        this.stopClerkVerificationCooldownTimer();
        const storageKey = this.getClerkVerificationCooldownStorageKey();
        try {
          const stored = JSON.parse(localStorage.getItem(storageKey) || "null");
          if (!stored?.until || stored.until <= Date.now()) {
            localStorage.removeItem(storageKey);
          }
        } catch (error) {
          localStorage.removeItem(storageKey);
        }
      }
    },

    startClerkVerificationCooldown(
      seconds = DEFAULT_CLERK_VERIFICATION_COOLDOWN_SECONDS
    ) {
      const duration = Math.max(1, Math.ceil(Number(seconds) || 0));
      const until = Date.now() + duration * 1000;
      this.clerkVerificationCooldownUntil = Math.max(
        this.clerkVerificationCooldownUntil,
        until
      );

      try {
        localStorage.setItem(
          this.getClerkVerificationCooldownStorageKey(),
          JSON.stringify({
            email: this.email.trim().toLowerCase(),
            until: this.clerkVerificationCooldownUntil,
          })
        );
      } catch (error) {
        console.warn("Impossibile salvare il cooldown Clerk", error);
      }

      this.stopClerkVerificationCooldownTimer();
      this.updateClerkVerificationCooldown();
      if (this.clerkVerificationCooldownRemaining > 0) {
        this.clerkVerificationCooldownTimer = setInterval(
          this.updateClerkVerificationCooldown,
          1000
        );
      }
    },

    restoreClerkVerificationCooldown() {
      try {
        const stored = JSON.parse(
          localStorage.getItem(this.getClerkVerificationCooldownStorageKey()) ||
            "null"
        );
        const currentEmail = this.email.trim().toLowerCase();

        if (
          stored?.email === currentEmail &&
          Number(stored?.until) > Date.now()
        ) {
          this.clerkVerificationCooldownUntil = Number(stored.until);
          this.stopClerkVerificationCooldownTimer();
          this.updateClerkVerificationCooldown();
          this.clerkVerificationCooldownTimer = setInterval(
            this.updateClerkVerificationCooldown,
            1000
          );
        }
      } catch (error) {
        localStorage.removeItem(this.getClerkVerificationCooldownStorageKey());
      }
    },

    setClerkVerificationRequestError(error, fallback) {
      if (!isClerkRateLimitError(error)) {
        this.errorMessage4 = this.formatClerkError(error, fallback);
        return;
      }

      const retryAfter = getClerkRetryAfterSeconds(error);
      this.startClerkVerificationCooldown(retryAfter);
      this.errorMessage4 = `Sono stati richiesti troppi codici. Attendi ${this.clerkVerificationCooldownRemaining} secondi e usa “Invia nuovo codice”.`;
    },

    editClerkRegistrationData() {
      this.clerkVerificationPending = false;
      this.clerkVerificationCode = "";
      this.clerkVerificationCodeSent = false;
      this.clerkVerificationNotice = "";
      this.riepilogoFinale = false;

      if (this.registrationType === "newStudio") {
        this.privacyPhase = false;
        this.newStudioAccountPhase = true;
      } else {
        this.studioTrovato = false;
      }

      this.clearAllMessages();
      this.resetRegistrationScroll();
    },

    resetForgotPasswordState() {
      this.forgotPasswordMode = false;
      this.forgotPasswordCodeSent = false;
      this.forgotPasswordEmail = "";
      this.forgotPasswordCode = "";
      this.forgotPasswordNewPassword = "";
      this.forgotPasswordConfirmPassword = "";
    },

    startForgotPassword() {
      this.clearAllMessages();
      this.forgotPasswordMode = true;
      this.forgotPasswordCodeSent = false;
      this.forgotPasswordEmail = this.email || "";
      this.forgotPasswordCode = "";
      this.forgotPasswordNewPassword = "";
      this.forgotPasswordConfirmPassword = "";
    },

    cancelForgotPassword() {
      this.clearAllMessages();
      this.resetForgotPasswordState();
    },

    async sendForgotPasswordCode() {
      if (!this.forgotPasswordEmail.trim()) {
        this.errorMessage1 = "Inserisci l'email dell'account.";
        return;
      }

      this.isLoading = true;
      this.errorMessage1 = "";

      try {
        const clerk = await getClerk();

        if (clerk.isSignedIn) {
          await signOutFromClerk();
          this.clerkSignedIn = false;
        }

        if (clerk.client?.resetSignIn) {
          clerk.client.resetSignIn();
        }

        const signIn = clerk.client.signIn;

        if (signIn.resetPasswordEmailCode?.sendCode) {
          await signIn.create({
            identifier: this.forgotPasswordEmail.trim(),
          });
          await signIn.resetPasswordEmailCode.sendCode();
        } else {
          await signIn.create({
            strategy: "reset_password_email_code",
            identifier: this.forgotPasswordEmail.trim(),
          });
        }

        this.forgotPasswordCodeSent = true;
        this.email = this.forgotPasswordEmail.trim();
      } catch (error) {
        this.errorMessage1 = this.formatClerkError(
          error,
          "Non riesco a inviare il codice di reset. Controlla l'email e riprova."
        );
      } finally {
        this.isLoading = false;
      }
    },

    async resetForgottenPassword() {
      if (!this.forgotPasswordCode.trim()) {
        this.errorMessage1 = "Inserisci il codice ricevuto via email.";
        return;
      }

      if (
        !this.forgotPasswordNewPassword ||
        this.forgotPasswordNewPassword !== this.forgotPasswordConfirmPassword
      ) {
        this.errorMessage1 = "Le password non coincidono.";
        return;
      }

      const forgotPasswordPolicyError = getPasswordPolicyError(
        this.forgotPasswordNewPassword,
        this.passwordPolicy
      );
      if (forgotPasswordPolicyError) {
        this.errorMessage1 = forgotPasswordPolicyError;
        return;
      }

      this.isLoading = true;
      this.errorMessage1 = "";

      try {
        const clerk = await getClerk();
        let signIn = clerk.client.signIn;

        if (signIn.resetPasswordEmailCode?.verifyCode) {
          await signIn.resetPasswordEmailCode.verifyCode({
            code: this.forgotPasswordCode.trim(),
          });
          await signIn.resetPasswordEmailCode.submitPassword({
            password: this.forgotPasswordNewPassword,
            signOutOfOtherSessions: true,
          });
        } else {
          signIn = await signIn.attemptFirstFactor({
            strategy: "reset_password_email_code",
            code: this.forgotPasswordCode.trim(),
            password: this.forgotPasswordNewPassword,
          });
        }

        if (signIn.status === "needs_second_factor") {
          this.errorMessage1 =
            "Password aggiornata, ma questo account richiede una verifica aggiuntiva non ancora gestita.";
          return;
        }

        if (signIn.status !== "complete" || !signIn.createdSessionId) {
          this.errorMessage1 =
            "Password aggiornata, ma accesso non completato. Torna al login e riprova.";
          return;
        }

        await this.activateClerkSession(clerk, signIn.createdSessionId);
        this.email = this.forgotPasswordEmail.trim();
        this.resetForgotPasswordState();
        await this.handleClerkSession();
      } catch (error) {
        this.errorMessage1 = this.formatClerkError(
          error,
          "Codice non valido o scaduto. Richiedi un nuovo codice."
        );
      } finally {
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
      }
    },

    beginPendingActivation(data = {}) {
      this.pendingActivation = true;
      this.pendingActivationVerificationPending = false;
      this.pendingActivationCode = "";
      this.pendingActivationFirstName = data.first_name || "";
      this.pendingActivationLastName = data.last_name || "";
      this.pendingActivationFiscalCode = "";
      this.pendingActivationInviteToken = data.invite_token || "";
      this.pendingActivationClerkTicket = data.clerk_ticket || "";
      this.pendingActivationStudioName = data.studio_nome || "";
      this.email = data.email || this.email;
      this.confirmPassword = "";
      this.isRegistered = false;
      this.errorMessage1 =
        data.message ||
        "Account creato dallo studio. Conferma la password per attivarlo.";
    },

    async loadInvitationFromToken(token) {
      this.isLoading = true;
      this.clearAllMessages();

      try {
        const response = await axios.get(`${API_BASE_URL}/invitations/lookup`, {
          params: { token },
        });

        this.beginPendingActivation({
          ...response.data,
          invite_token: token,
          message: "",
        });
        this.errorMessage1 = "";
      } catch (error) {
        this.errorMessage1 =
          error.response?.data?.message ||
          "Invito non valido o scaduto. Chiedi allo studio di reinviarlo.";
      } finally {
        this.isLoading = false;
      }
    },

    loadClerkInvitationFromTicket(ticket) {
      this.clearAllMessages();
      this.beginPendingActivation({
        clerk_ticket: ticket,
        message: "",
      });
      this.errorMessage1 = "";
      this.justRegistered = false;
    },

    async startPendingActivationVerification() {
      if (
        (!this.pendingActivationClerkTicket && !this.email) ||
        !this.pendingActivationFiscalCode ||
        !this.password ||
        !this.confirmPassword
      ) {
        this.errorMessage1 =
          "Inserisci il Codice Fiscale e conferma la password.";
        return;
      }

      if (this.password !== this.confirmPassword) {
        this.errorMessage1 = "Le password non coincidono.";
        return;
      }

      const passwordPolicyError = getPasswordPolicyError(
        this.password,
        this.passwordPolicy
      );
      if (passwordPolicyError) {
        this.errorMessage1 = passwordPolicyError;
        return;
      }

      this.isLoading = true;
      this.errorMessage1 = "";

      try {
        await axios.post(`${API_BASE_URL}/registration/validate`, {
          registration_type: "existingStudio",
          codice_fiscale: this.pendingActivationFiscalCode.trim().toUpperCase(),
        });
      } catch (error) {
        this.errorMessage1 =
          error.response?.data?.message ||
          "Controlla il Codice Fiscale inserito.";
        this.isLoading = false;
        return;
      }

      if (this.pendingActivationClerkTicket) {
        await this.completePendingActivationWithClerkTicket();
        return;
      }

      try {
        const clerk = await getClerk();

        if (clerk.isSignedIn) {
          const profile = getClerkProfile(clerk);
          if (
            profile.email &&
            profile.email.toLowerCase().trim() ===
              this.email.toLowerCase().trim()
          ) {
            this.clerkSignedIn = true;
            await this.handleClerkSession();
            return;
          }

          await signOutFromClerk();
          this.clerkSignedIn = false;
        }

        if (clerk.client?.resetSignUp) {
          clerk.client.resetSignUp();
        }

        await clerk.client.signUp.create({
          emailAddress: this.email,
          password: this.password,
          firstName: this.pendingActivationFirstName,
          lastName: this.pendingActivationLastName,
        });

        await clerk.client.signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });

        this.pendingActivationCode = "";
        this.pendingActivationVerificationPending = true;
      } catch (error) {
        this.errorMessage1 = this.formatClerkError(
          error,
          "Non riesco a inviare il codice di verifica."
        );
      } finally {
        this.isLoading = false;
      }
    },

    async completePendingActivationWithClerkTicket() {
      try {
        const clerk = await getClerk();

        if (clerk.isSignedIn) {
          await signOutFromClerk();
          this.clerkSignedIn = false;
        }

        if (clerk.client?.resetSignUp) {
          clerk.client.resetSignUp();
        }

        const signUp = await clerk.client.signUp.create({
          strategy: "ticket",
          ticket: this.pendingActivationClerkTicket,
          password: this.password,
          firstName: this.pendingActivationFirstName || undefined,
          lastName: this.pendingActivationLastName || undefined,
        });

        if (signUp.status !== "complete" || !signUp.createdSessionId) {
          this.errorMessage1 =
            "Invito verificato, ma attivazione account non completata.";
          return;
        }

        await this.activateClerkSession(clerk, signUp.createdSessionId);
        this.pendingActivation = false;
        this.pendingActivationVerificationPending = false;

        const response = await axios.post(
          `${API_BASE_URL}/clerk/accept_invite`,
          {
            codice_fiscale: this.pendingActivationFiscalCode
              .trim()
              .toUpperCase(),
          },
          { headers: await this.getClerkAuthHeaders() }
        );

        this.applyAuthSession(response, this.email);
        await this.loadAndCacheSettings();
        await this.navigateToHomeAfterAuth();
      } catch (error) {
        this.errorMessage1 = this.formatClerkError(
          error,
          "Invito non valido o scaduto. Chiedi allo studio di reinviarlo."
        );
      } finally {
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
      }
    },

    async verifyPendingActivationCode() {
      if (!this.pendingActivationCode.trim()) {
        this.errorMessage1 = "Inserisci il codice ricevuto via email.";
        return;
      }

      this.isLoading = true;
      this.errorMessage1 = "";

      try {
        const clerk = await getClerk();
        const signUp =
          await clerk.client.signUp.attemptEmailAddressVerification({
            code: this.pendingActivationCode.trim(),
          });

        if (signUp.status !== "complete" || !signUp.createdSessionId) {
          this.errorMessage1 =
            "Codice verificato, ma attivazione Clerk non completata.";
          return;
        }

        await this.activateClerkSession(clerk, signUp.createdSessionId);
        this.pendingActivation = false;
        this.pendingActivationVerificationPending = false;
        this.pendingActivationCode = "";
        if (this.pendingActivationInviteToken) {
          const response = await axios.post(
            `${API_BASE_URL}/clerk/accept_invite`,
            {
              token: this.pendingActivationInviteToken,
              codice_fiscale: this.pendingActivationFiscalCode
                .trim()
                .toUpperCase(),
            },
            { headers: await this.getClerkAuthHeaders() }
          );
          this.applyAuthSession(response, this.email);
          await this.loadAndCacheSettings();
          await this.navigateToHomeAfterAuth();
          return;
        }

        await this.handleClerkSession();
      } catch (error) {
        this.errorMessage1 = this.formatClerkError(
          error,
          "Codice non valido o scaduto."
        );
      } finally {
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
      }
    },

    cancelPendingActivation() {
      this.pendingActivation = false;
      this.pendingActivationVerificationPending = false;
      this.pendingActivationCode = "";
      this.pendingActivationFiscalCode = "";
      this.confirmPassword = "";
      this.pendingActivationInviteToken = "";
      this.pendingActivationClerkTicket = "";
      this.pendingActivationStudioName = "";
      this.clearAllMessages();
    },

    async loginWithClerk() {
      if (!this.email || !this.password) {
        this.errorMessage1 = "Compila tutti i campi!";
        setTimeout(() => (this.errorMessage1 = ""), 3000);
        return;
      }

      this.isLoading = true;

      try {
        disableGuestMode();

        const legacyLoginCompleted = await this.tryLegacyLoginMigration();
        if (legacyLoginCompleted) {
          return;
        }

        const clerk = await getClerk();

        if (clerk.isSignedIn) {
          const currentProfile = getClerkProfile(clerk);
          const currentEmail = (currentProfile.email || "")
            .toLowerCase()
            .trim();
          const requestedEmail = this.email.toLowerCase().trim();

          if (currentEmail && currentEmail === requestedEmail) {
            this.clerkSignedIn = true;
            await this.handleClerkSession();
            return;
          }

          await signOutFromClerk();
          this.clerkSignedIn = false;
        }

        await this.completeClerkPasswordSignIn(clerk);
      } catch (error) {
        if (this.isAlreadySignedInClerkError(error)) {
          const completed = await this.tryUseCurrentClerkSession();
          if (completed) {
            return;
          }

          const retried = await this.retryClerkLoginAfterActiveSession();
          if (retried) {
            return;
          }

          this.errorMessage1 =
            "Hai già una sessione attiva. Aggiorna la pagina o torna alla home.";
          return;
        }

        this.errorMessage1 = this.formatClerkError(
          error,
          "Email o password non validi."
        );
        setTimeout(() => (this.errorMessage1 = ""), 5000);
      } finally {
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
      }
    },

    isAlreadySignedInClerkError(error) {
      const message = this.getRawClerkErrorMessage(error).toLowerCase();
      return message.includes("already signed in");
    },

    async completeClerkPasswordSignIn(clerkInstance = null) {
      const clerk = clerkInstance || (await getClerk());

      if (clerk.client?.resetSignIn) {
        clerk.client.resetSignIn();
      }

      const signIn = await clerk.client.signIn.create({
        identifier: this.email,
        password: this.password,
      });

      if (signIn.status !== "complete" || !signIn.createdSessionId) {
        if (signIn.status === "needs_client_trust") {
          this.errorMessage1 =
            "Clerk sta ancora richiedendo Device Trust. Disattivalo su Clerk per usare login solo email e password.";
          return false;
        }

        this.errorMessage1 =
          signIn.status === "needs_new_password"
            ? "Devi impostare una nuova password per completare l'accesso."
            : `Accesso non completato (${signIn.status}). Controlla email e password.`;
        return false;
      }

      await this.activateClerkSession(clerk, signIn.createdSessionId);
      await this.handleClerkSession();
      return true;
    },

    async tryUseCurrentClerkSession() {
      try {
        const clerk = await getClerk();
        if (!clerk.isSignedIn && !clerk.session) {
          return false;
        }

        const profile = getClerkProfile(clerk);
        if (
          profile.email &&
          profile.email.toLowerCase().trim() !== this.email.toLowerCase().trim()
        ) {
          await signOutFromClerk();
          this.clerkSignedIn = false;
          return false;
        }

        this.clerkSignedIn = true;
        await this.handleClerkSession();
        return true;
      } catch (bridgeError) {
        console.error("Errore recupero sessione Clerk attiva", bridgeError);
        this.errorMessage1 =
          "Sessione già attiva. Sto completando l'accesso a MyStudio.";
        try {
          this.clerkSignedIn = true;
          await this.handleClerkSession();
          return true;
        } catch (fallbackError) {
          console.error("Errore completamento sessione attiva", fallbackError);
        }
        return false;
      }
    },

    async retryClerkLoginAfterActiveSession() {
      try {
        const clerk = await getClerk();

        if (typeof clerk.signOut === "function") {
          await clerk.signOut();
        } else {
          await signOutFromClerk();
        }

        this.clerkSignedIn = false;
        return await this.completeClerkPasswordSignIn(clerk);
      } catch (retryError) {
        console.error(
          "Retry login dopo sessione Clerk attiva non riuscito",
          retryError
        );
        return false;
      }
    },

    async tryLegacyLoginMigration() {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/clerk/migrate_legacy_login`,
          {
            email: this.email,
            password: this.password,
          },
          { skipGlobalErrorLog: true }
        );

        this.applyAuthSession(response, this.email);
        await this.loadAndCacheSettings();
        await this.navigateToHomeAfterAuth();
        return true;
      } catch (error) {
        if (
          error.response?.status === 428 &&
          error.response?.data?.requires_activation
        ) {
          if (this.pendingActivationInviteToken) {
            this.beginPendingActivation({
              ...error.response.data,
              invite_token: this.pendingActivationInviteToken,
            });
          } else {
            this.errorMessage1 =
              "Account non ancora attivato. Apri il link di invito ricevuto dallo studio oppure chiedi un nuovo invito.";
          }
          return true;
        }

        if (error.response?.status === 409) {
          return false;
        }

        if (error.response?.status && error.response.status >= 500) {
          this.errorMessage1 =
            error.response?.data?.message ||
            "Migrazione account non riuscita. Riprova tra poco.";
          return true;
        }

        return false;
      }
    },

    async startClerkRegistrationVerification() {
      this.restoreClerkVerificationCooldown();
      if (this.clerkVerificationCooldownActive) {
        this.errorMessage4 = `Attendi ${this.clerkVerificationCooldownRemaining} secondi prima di richiedere un altro codice.`;
        return;
      }

      this.isLoading = true;
      this.isRegistratiDisabled = true;
      this.errorMessage4 = "";
      this.clerkVerificationNotice = "";
      this.clerkVerificationCodeSent = false;
      let signUpCreated = false;

      try {
        const clerk = await getClerk();

        if (clerk.isSignedIn) {
          await signOutFromClerk();
          this.clerkSignedIn = false;
        }

        if (clerk.client?.resetSignUp) {
          clerk.client.resetSignUp();
        }

        await clerk.client.signUp.create({
          emailAddress: this.email,
          password: this.password,
          firstName: this.firstName,
          lastName: this.lastName,
        });
        signUpCreated = true;

        await clerk.client.signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });

        this.clerkVerificationCode = "";
        this.clerkVerificationPending = true;
        this.clerkVerificationCodeSent = true;
        this.clerkVerificationNotice =
          "Codice inviato. Puoi usarlo per 10 minuti; controlla anche la cartella spam.";
        this.startClerkVerificationCooldown();
        this.errorMessage4 = "";
      } catch (error) {
        if (signUpCreated && isClerkRateLimitError(error)) {
          this.clerkVerificationPending = true;
          this.clerkVerificationCodeSent = false;
        }
        this.setClerkVerificationRequestError(
          error,
          "Non riesco a inviare il codice di verifica."
        );
      } finally {
        this.isLoading = false;
        this.isRegistratiDisabled = false;
      }
    },

    async resendClerkRegistrationCode() {
      this.restoreClerkVerificationCooldown();
      if (this.clerkVerificationCooldownActive) {
        this.errorMessage4 = `Attendi ${this.clerkVerificationCooldownRemaining} secondi prima di richiedere un altro codice.`;
        return;
      }

      if (this.isLoading || !this.clerkVerificationPending) return;

      this.isLoading = true;
      this.errorMessage4 = "";
      this.clerkVerificationNotice = "";

      try {
        const clerk = await getClerk();
        await clerk.client.signUp.prepareEmailAddressVerification({
          strategy: "email_code",
        });

        this.clerkVerificationCode = "";
        this.clerkVerificationCodeSent = true;
        this.clerkVerificationNotice =
          "Nuovo codice inviato. Il codice precedente non è più valido.";
        this.startClerkVerificationCooldown();
      } catch (error) {
        this.setClerkVerificationRequestError(
          error,
          "Non riesco a inviare un nuovo codice di verifica."
        );
      } finally {
        this.isLoading = false;
      }
    },

    async verifyClerkRegistrationCode() {
      if (!this.clerkVerificationCode.trim()) return;

      this.isLoading = true;
      this.errorMessage4 = "";

      try {
        const clerk = await getClerk();
        const signUp =
          await clerk.client.signUp.attemptEmailAddressVerification({
            code: this.clerkVerificationCode.trim(),
          });

        if (signUp.status !== "complete" || !signUp.createdSessionId) {
          this.errorMessage4 =
            "Codice verificato, ma registrazione Clerk non completata.";
          return;
        }

        await this.activateClerkSession(clerk, signUp.createdSessionId);
        this.clerkSignUpComplete = true;
        this.clerkVerificationPending = false;
        await this.finalizeRegistration();
      } catch (error) {
        this.errorMessage4 = this.formatClerkError(
          error,
          "Codice non valido o scaduto."
        );
      } finally {
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
      }
    },

    prefillClerkOnboarding(data = {}) {
      this.email = data.email || this.email;
      this.firstName = data.first_name || this.firstName;
      this.lastName = data.last_name || this.lastName;
      this.password = "";
      this.confirmPassword = "";
      this.clerkSignUpComplete = true;
      this.clerkVerificationPending = false;
      this.clerkVerificationCode = "";
      this.isRegistered = true;
      this.justRegistered = false;
      this.clearAllMessages();
    },

    async handleClerkSession() {
      if (this.clerkBridgeInProgress) {
        return;
      }

      this.clerkBridgeInProgress = true;
      this.isLoading = true;
      this.clearAllMessages();

      try {
        disableGuestMode();
        const clerk = await getClerk();
        const profile = getClerkProfile(clerk);
        const headers = await this.getClerkAuthHeaders();
        const response = await axios.post(
          `${API_BASE_URL}/clerk/session`,
          {},
          { headers }
        );

        this.applyAuthSession(response, profile.email);
        await this.loadAndCacheSettings();
        await this.navigateToHomeAfterAuth();
      } catch (error) {
        if (error.response?.data?.account_not_linked) {
          await signOutFromClerk();
          this.clerkSignedIn = false;
          this.errorMessage1 =
            error.response?.data?.message ||
            "Account non attivo su MyStudio. Registrati di nuovo o contatta l'amministratore.";
          return;
        }

        if (
          error.response?.status === 404 &&
          error.response?.data?.needs_onboarding
        ) {
          this.prefillClerkOnboarding(error.response.data);
          return;
        }

        this.errorMessage1 =
          error.response?.data?.message ||
          "Non riesco a completare l'accesso con Clerk.";
      } finally {
        this.clerkBridgeInProgress = false;
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
      }
    },

    async disconnectClerkAccount() {
      this.clearAllMessages();
      this.isLoading = true;

      try {
        await signOutFromClerk();
        this.clerkSignedIn = false;
      } catch (error) {
        console.error("Errore logout Clerk", error);
        this.errorMessage1 = "Non riesco a scollegare l'account Clerk.";
      } finally {
        this.isLoading = false;
      }
    },

    async loadAndCacheSettings() {
      try {
        const res = await axios.get(`${API_BASE_URL}/settings`);
        localStorage.setItem("userSettings", JSON.stringify(res.data));
      } catch (err) {
        console.warn("Impossibile caricare impostazioni", err);
        const defaultSettings = {
          flag_saldoProspettiPaghe: true,
          flag_saldoF24: true,
          flag_accorpato: false,
        };
        localStorage.setItem("userSettings", JSON.stringify(defaultSettings));
      }
    },

    async loginUser() {
      this.clearAllMessages();
      if (this.clerkEnabled) {
        await this.loginWithClerk();
        return;
      }

      if (!this.email || !this.password) {
        this.errorMessage1 = "Compila tutti i campi!";
        setTimeout(() => (this.errorMessage1 = ""), 3000);
        return;
      }

      this.isLoading = true;

      try {
        disableGuestMode();
        const response = await axios.post(`${API_BASE_URL}/login`, {
          email: this.email,
          password: this.password,
        });

        this.applyAuthSession(response, this.email);
        await this.loadAndCacheSettings();
        await this.navigateToHomeAfterAuth();
      } catch (error) {
        this.errorMessage1 =
          error.response?.status === 401
            ? "Email o password non validi."
            : "Errore di connessione al server.";
        setTimeout(() => (this.errorMessage1 = ""), 3000);
      } finally {
        setTimeout(() => {
          if (!this.authNavigationInProgress) {
            this.isLoading = false;
          }
        }, 600);
      }
    },

    async accessAsGuest() {
      this.clearAllMessages();
      this.isGuestLoading = true;

      const minimumGuestLoading = new Promise((resolve) =>
        setTimeout(resolve, 1000)
      );

      try {
        disableGuestMode();
        await Promise.all([
          axios.post(`${API_BASE_URL}/logout`).catch((error) => {
            console.warn("Logout preventivo ospite non riuscito", error);
          }),
          minimumGuestLoading,
        ]);
      } catch (error) {
        console.warn("Accesso ospite non riuscito completamente", error);
      } finally {
        enableGuestMode();
        this.isGuestLoading = false;
        this.$router.push("/home");
      }
    },

    async goToRiepilogo() {
      this.clearAllMessages();

      if (!this.registrationType) {
        this.errorMessage2 = "Seleziona il tipo di registrazione.";
        return;
      }

      if (!this.nomeStudio.trim()) {
        this.errorMessage3 = "Inserisci il nome dello studio.";
        return;
      }

      if (!this.email) {
        this.errorMessage3 = "Inserisci un indirizzo email.";
        return;
      }

      if (!this.codiceFiscale.trim()) {
        this.errorMessage3 = "Inserisci il tuo Codice Fiscale.";
        return;
      }

      if (
        this.registrationType === "newStudio" &&
        (!this.partitaIva.trim() ||
          !this.indirizzoSede.trim() ||
          !this.cap.trim() ||
          !this.citta.trim() ||
          !this.provincia.trim())
      ) {
        this.errorMessage3 =
          "Completa Partita IVA e indirizzo della sede dello studio.";
        return;
      }

      if (!this.password || this.password !== this.confirmPassword) {
        this.errorMessage3 = "Le password non corrispondono o sono mancanti.";
        return;
      }

      this.isLoading = true;
      this.isRegistratiDisabled = true;

      try {
        if (this.registrationType === "existingStudio") {
          const response = await axios.get(`${API_BASE_URL}/studi/check`, {
            params: {
              nome: this.nomeStudio,
              key: this.registrationKey,
            },
          });

          await new Promise((r) => setTimeout(r, 800));

          if (!response.data.exists || !response.data.id) {
            this.errorMessage4 =
              "Studio non trovato. Verifica il nome inserito.";
            setTimeout(() => (this.errorMessage4 = ""), 3000);
            return;
          }

          this.existingStudioId = response.data.id;
          this.nomeStudioTrovato = this.nomeStudio;
          this.studioTrovato = true;
        } else {
          this.riepilogoFinale = true;
        }
      } catch (error) {
        this.errorMessage4 =
          error.response?.data?.message ||
          "Errore durante il controllo studio.";
      } finally {
        this.isLoading = false;
        this.isRegistratiDisabled = false;
      }
    },

    async advanceRegistration() {
      if (
        this.registrationType === "newStudio" &&
        !this.newStudioAccountPhase
      ) {
        await this.goToNewStudioAccountPhase();
        return;
      }

      await this.goToPrivacyPhase();
    },

    async goToNewStudioAccountPhase() {
      this.clearAllMessages();

      if (!this.nomeStudio.trim()) {
        this.errorMessage3 = "Inserisci il nome dello studio.";
        return;
      }

      if (
        !this.partitaIva.trim() ||
        !this.indirizzoSede.trim() ||
        !this.cap.trim() ||
        !this.citta.trim() ||
        !this.provincia.trim()
      ) {
        this.errorMessage3 =
          "Completa Partita IVA e indirizzo della sede dello studio.";
        return;
      }

      this.isLoading = true;
      this.isRegistratiDisabled = true;

      try {
        await this.validateRegistrationRegistry("studio");
        this.newStudioAccountPhase = true;
        this.resetRegistrationScroll();
      } catch (error) {
        this.errorMessage3 =
          error.response?.data?.message ||
          "Controlla Partita IVA e dati della sede.";
      } finally {
        this.isLoading = false;
        this.isRegistratiDisabled = false;
      }
    },

    async goToPrivacyPhase() {
      this.clearAllMessages();

      if (!this.nomeStudio.trim()) {
        this.errorMessage3 = "Inserisci il nome dello studio.";
        return;
      }

      if (!this.email) {
        this.errorMessage3 = "Inserisci un indirizzo email.";
        return;
      }

      if (!this.codiceFiscale.trim()) {
        this.errorMessage3 = "Inserisci il tuo Codice Fiscale.";
        return;
      }

      if (
        this.registrationType === "newStudio" &&
        (!this.partitaIva.trim() ||
          !this.indirizzoSede.trim() ||
          !this.cap.trim() ||
          !this.citta.trim() ||
          !this.provincia.trim())
      ) {
        this.errorMessage3 =
          "Completa Partita IVA e indirizzo della sede dello studio.";
        return;
      }

      if (this.isFormInvalid) {
        this.errorMessage3 = "Controlla password e conferma.";
        return;
      }

      this.isLoading = true;
      this.isRegistratiDisabled = true;

      try {
        await this.validateRegistrationRegistry("account");

        if (this.registrationType === "existingStudio") {
          const { data } = await axios.get(`${API_BASE_URL}/studi/check`, {
            params: { nome: this.nomeStudio, key: this.registrationKey },
          });

          if (!data.exists || !data.id) {
            this.errorMessage3 = "Studio non trovato. Verifica nome e chiave.";
            return;
          }

          this.existingStudioId = data.id;
          this.nomeStudioTrovato = this.nomeStudio;
          this.studioTrovato = true;
        } else {
          this.privacyPhase = true;
        }
      } catch (error) {
        this.errorMessage3 =
          error.response?.data?.message ||
          "Controlla il Codice Fiscale inserito.";
      } finally {
        this.isLoading = false;
        this.isRegistratiDisabled = false;
      }
    },

    goToRiepilogoFromPrivacy() {
      this.clearAllMessages();
      if (!this.privacyAccettata) {
        this.errorMessage4 = "È necessario accettare l'informativa privacy.";
        return;
      }
      if (!this.terminiAccettati) {
        this.errorMessage4 =
          "È necessario accettare i Termini e condizioni di utilizzo.";
        return;
      }
      this.riepilogoFinale = true;
    },

    async validateRegistrationRegistry(validationScope = "complete") {
      await axios.post(`${API_BASE_URL}/registration/validate`, {
        registration_type: this.registrationType,
        validation_scope: validationScope,
        codice_fiscale: this.codiceFiscale.trim().toUpperCase(),
        partita_iva: this.partitaIva.trim(),
        indirizzo_sede: this.indirizzoSede.trim(),
        cap: this.cap.trim(),
        citta: this.citta.trim(),
        provincia: this.provincia.trim().toUpperCase(),
      });
    },

    async finalizeRegistration() {
      if (this.isLoading || this.isRegistratiDisabled) return;

      this.restoreClerkVerificationCooldown();
      if (
        this.clerkEnabled &&
        !this.clerkSignUpComplete &&
        this.clerkVerificationCooldownActive
      ) {
        this.errorMessage4 = `Attendi ${this.clerkVerificationCooldownRemaining} secondi prima di richiedere un altro codice.`;
        return;
      }

      this.clearAllMessages();
      this.isLoading = true;
      this.isRegistratiDisabled = true;

      try {
        if (this.registrationType === "newStudio") {
          if (!this.privacyAccettata) {
            this.errorMessage4 = "Devi accettare l'informativa privacy.";
            return;
          }

          if (!this.terminiAccettati) {
            this.errorMessage4 =
              "Devi accettare i Termini e condizioni di utilizzo.";
            return;
          }

          if (this.clerkEnabled && !this.clerkSignUpComplete) {
            await this.validateRegistrationRegistry();
            await this.startClerkRegistrationVerification();
            return;
          }

          const payload = {
            email: this.email,
            nome_studio: this.nomeStudio,
            codice_fiscale: this.codiceFiscale.trim().toUpperCase(),
            partita_iva: this.partitaIva.trim(),
            indirizzo_sede: this.indirizzoSede.trim(),
            cap: this.cap.trim(),
            citta: this.citta.trim(),
            provincia: this.provincia.trim().toUpperCase(),
            crediti_disponibili: 0,
            euro_spesi: 0,
            first_name: this.firstName,
            last_name: this.lastName,
          };
          let url = `${API_BASE_URL}/register_studio`;
          let config = undefined;

          if (this.clerkEnabled) {
            url = `${API_BASE_URL}/clerk/register_studio`;
            config = { headers: await this.getClerkAuthHeaders() };
          } else {
            payload.password = this.password;
            payload.confirm_password = this.confirmPassword;
          }

          const res = await axios.post(url, payload, config);

          if (res.status === 201) {
            if (this.clerkEnabled) {
              this.applyAuthSession(res, this.email);
              await this.loadAndCacheSettings();
              await this.navigateToHomeAfterAuth();
              return;
            }

            this.successMessage =
              "Studio e account utente creati con successo!";
          } else {
            this.errorMessage4 =
              res.data?.message || "Errore nella registrazione.";
          }
        } else {
          if (!this.existingStudioId) {
            this.errorMessage4 = "Errore interno: ID studio mancante.";
            return;
          }

          if (!this.privacyAccettata) {
            this.errorMessage4 =
              "Devi dichiarare di aver letto l'informativa privacy.";
            return;
          }

          if (!this.terminiAccettati) {
            this.errorMessage4 =
              "Devi accettare i Termini e condizioni di utilizzo.";
            return;
          }

          if (this.clerkEnabled && !this.clerkSignUpComplete) {
            await this.validateRegistrationRegistry();
            await this.startClerkRegistrationVerification();
            return;
          }

          const payload = {
            email: this.email,
            registration_key: this.registrationKey,
            codice_fiscale: this.codiceFiscale.trim().toUpperCase(),
            first_name: this.firstName,
            last_name: this.lastName,
          };
          let url = `${API_BASE_URL}/utenti`;
          let config = undefined;

          if (this.clerkEnabled) {
            url = `${API_BASE_URL}/clerk/utenti`;
            config = { headers: await this.getClerkAuthHeaders() };
          } else {
            payload.password = this.password;
          }

          const res = await axios.post(url, payload, config);

          if (res.data.success || res.status === 201) {
            if (this.clerkEnabled) {
              this.applyAuthSession(res, this.email);
              await this.loadAndCacheSettings();
              await this.navigateToHomeAfterAuth();
              return;
            }

            this.successMessage = "Utente creato con successo!";
          } else {
            this.errorMessage4 =
              res.data?.message || "Errore nella creazione utente.";
          }
        }
      } catch (err) {
        this.errorMessage4 =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.response?.data?.detail ||
          err.message ||
          "Errore durante la registrazione.";
      } finally {
        if (!this.authNavigationInProgress) {
          this.isLoading = false;
        }
        this.isRegistratiDisabled = false;
      }
    },
  },
};
</script>

<style scoped>
/* Layout principale */
.auth-wrapper {
  min-height: 100dvh;
  box-sizing: border-box;
  padding: clamp(1.25rem, 3vh, 2.5rem) clamp(1rem, 3vw, 3rem);
  background: radial-gradient(
      circle at 16% 12%,
      rgba(255, 255, 255, 0.96),
      transparent 34%
    ),
    linear-gradient(135deg, #f4faf7 0%, #e8f4ed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  font-family: "Segoe UI", sans-serif;
}
.maincontainer {
  position: relative;
  width: min(90vw, 1200px);
  height: min(680px, calc(100dvh - 3rem));
  min-height: 600px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 28px 70px rgba(28, 67, 51, 0.18),
    0 8px 24px rgba(27, 52, 42, 0.1);
  background: #fff;
}
.auth-container {
  position: relative;
  display: grid;
  grid-template-columns: minmax(380px, 48%) minmax(0, 52%);
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: inherit;
  overflow: hidden;
  background: #fff;
  transition: grid-template-columns 0.45s ease;
}

.auth-container.registration-active {
  grid-template-columns: minmax(380px, 43%) minmax(0, 57%);
}

.brand-panel,
.form-panel {
  width: auto;
  min-width: 0;
  position: relative;
}
.registration-form-fade .fade-enter-active,
.registration-form-fade .fade-leave-active {
  transition: opacity 0.15s ease;
}

.registration-form-fade .fade-enter,
.registration-form-fade .fade-leave-to {
  opacity: 0;
}
.brand-panel {
  z-index: 1;
  background: linear-gradient(
      rgba(239, 249, 243, 0.08),
      rgba(231, 244, 236, 0.08)
    ),
    var(--login-background) center center / cover no-repeat;
  color: #083e31;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(2rem, 4vw, 4rem);
  overflow: hidden;
}

.brand-content {
  position: relative;
  z-index: 1;
  width: 100%;
  text-align: center;
  max-width: 470px;
}
.centered-form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 100%;
}
.scrollable-form {
  box-sizing: border-box;
  max-width: none !important;
  height: 100%;
  max-height: 100%;
  padding: clamp(2.1rem, 4vh, 3.5rem) clamp(2.25rem, 4vw, 4.5rem) 2rem;
  overflow-x: hidden;
  overflow-y: auto;
}
.logo {
  width: 130px;
  margin-bottom: 1.25rem;
}
h1 {
  font-size: 3rem;
  margin: 0 0 0.5rem;
  color: #063e31;
  font-weight: 700;
  letter-spacing: normal;
}
.tagline {
  font-size: 1.3rem;
  opacity: 1;
  margin: 0 0 3rem;
  font-style: italic;
  color: #174c3d;
}

.home-link {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  background: rgba(56, 74, 65, 0.38);
  padding: 12px 24px;
  border-radius: 30px;
  color: white;
  text-decoration: none;
  font-weight: 650;
  transition: 0.3s;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 8px 24px rgba(25, 55, 43, 0.12);
}

.home-link:hover {
  background: white;
  color: #2e7d32;
  transform: translateY(-2px);
}

.logo-mini {
  width: 24px;
  filter: brightness(0) invert(1);
  transition: 0.3s;
}
.home-link:hover .logo-mini {
  filter: none;
}
/* Pannello form */
.form-panel {
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-height: 0;
}

.form-wrapper {
  width: 100%;
  max-width: 500px;
  box-sizing: border-box;
}

.form-wrapper.registration-choice-form {
  max-width: none;
}
.scroll-spacer {
  height: 1rem;
  flex-shrink: 0;
}
.auth-form-registration {
  position: relative;
  width: 100%;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 100%;
  padding-top: 0;
  margin: 0 auto;
}

.auth-form-registration h2 {
  margin-top: 2rem;
}
.auth-form h2 {
  font-size: 2rem;
  color: #1b5e20;
  margin-bottom: 0.5rem;
  text-align: center;
}
.title-large {
  font-size: 2rem;
  margin-bottom: 2rem;
}

.title-small {
  font-size: 1.7rem !important;
  margin-bottom: 1.5rem !important;
}

.form-subtitle {
  color: #666;
  text-align: center;
  margin-bottom: 2rem;
}

/* Input e pulsanti */
.input-group {
  margin-bottom: 1rem;
  position: relative;
}
.input-group label {
  text-align: left;
}
label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.input-field {
  width: 92%;
  padding: 14px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s;
}

.input-field:focus {
  outline: none;
  border-color: #4caf50;
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.1);
}

.password-wrapper {
  position: relative;
}

.eye-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #999;
  font-size: 1.1rem;
}

.eye-icon:hover {
  color: #4caf50;
}
.confirmbutton {
  width: 80% !important;
}
.btn-primary {
  width: 100%;
  padding: 14px;
  background: #2e7d32;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  margin-top: 1rem;
}

.btn-primary:hover:not(:disabled) {
  background: #1b5e20;
  transform: translateY(-2px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.password-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 96%;
}

.forgot-password-link {
  border: 0;
  background: transparent;
  color: #2e7d32;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;
}

.forgot-password-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-guest {
  width: 100%;
  margin-top: 0.75rem;
  padding: 13px 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  background: #f3f6f4;
  color: #275745;
  border: 1px solid #cddbd3;
  border-radius: 12px;
  font-size: 1.02rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
}

.btn-guest:hover:not(:disabled) {
  background: #e7f0eb;
  border-color: #9fbcad;
  transform: translateY(-1px);
}

.btn-guest:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #eee;
  color: #333;
  margin-left: 10px;
  width: 8rem;
  width: 35%;
  padding: 14px;
  /* background: #2e7d32; */
  /* color: white; */
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.3s;
  margin-top: 1rem;
}

.verification-box .btn-primary,
.verification-box .btn-secondary {
  font-size: 0.9rem;
}

.verification-box .actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
}

.verification-box .actions .btn-primary,
.verification-box .actions .btn-secondary {
  width: auto;
}

.verification-box .actions .btn-secondary {
  min-width: 9rem;
  margin-left: 0;
  white-space: nowrap;
}

.verification-secondary-actions {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.verification-secondary-actions .btn-secondary {
  margin-top: 0;
}

.verification-box .verification-notice {
  margin: 0 0 1rem;
  color: #1b5e20;
  text-align: center;
}

.link-btn,
.privacy-link-btn {
  background: none;
  border: none;
  color: #2e7d32;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
}

.privacy-link-btn {
  font-size: 1rem;
}

.privacy-link-btn:hover {
  color: #1b5e20;
}

.switch-text {
  text-align: center;
  color: #666;
  margin-top: 1.5rem;
}

/* Card selezione tipo */
.type-selection {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin: 2.5rem auto;
  max-width: 780px;
  padding: 0 1rem;
}

.type-card {
  flex: 1;
  min-width: 160px;
  padding: 2rem 1.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fafafa;
}

.type-card:hover {
  border-color: #4caf50;
  transform: translateY(-8px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.type-card.selected {
  border-color: #2e7d32;
  background: #e8f5e9;
  box-shadow: 0 10px 30px rgba(46, 125, 50, 0.15);
}

.type-card .card-title {
  font-size: 1.35rem;
  margin-bottom: 1rem;
}
.registration-form {
  text-align: left;
  transform: scale(0.95);
  margin-top: 0rem;
}

/* Alert e messaggi */
.alert {
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.alert.error {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
  margin-top: 1rem;
  text-align: center;
}
.alert.success {
  background: #e8f5e9;
  /* color: #1b5e20;
  border: 1px solid #a5d6a7; */
}

.field-error {
  color: #d32f2f;
  font-size: 0.85rem;
  margin-top: 0.4rem;
}

.password-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.8rem;
  margin: 0.45rem 0 0;
  padding: 0;
  color: #a33a3a;
  font-size: 0.8rem;
  list-style: none;
}

.password-requirements li.valid {
  color: #247a3d;
}

/* Forza password */
.strength-meter {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 0.5rem;
}

.strength-meter .bar {
  flex: 1;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.fill {
  height: 100%;
  width: 25%;
  transition: all 0.3s;
}

.fill.weak {
  background: #ff5722;
  width: 30%;
}
.fill.medium {
  background: #ff9800;
  width: 55%;
}
.fill.strong {
  background: #8bc34a;
  width: 80%;
}
.fill.very-strong {
  background: #4caf50;
  width: 100%;
}

.label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  min-width: 60px;
  text-align: right;
}

/* Riepilogo */
.summary-box {
  background: #f8fff9;
  border: 2px solid #a5d6a7;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  margin: 1rem 0;
  margin-left: -2rem;
  width: 26rem;
}

.summary-box h3 {
  color: #1b5e20;
  margin-bottom: 1rem;
}

.verification-box {
  margin-top: 1.25rem;
  padding: 1.25rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(123, 158, 132, 0.18);
  text-align: left;
}

.verification-box h4 {
  margin: 0 0 0.5rem;
  color: #1f5137;
  text-align: center;
}

.verification-box p {
  color: #4b5f54;
  line-height: 1.45;
  margin: 0 0 1rem;
  text-align: center;
}

.actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1rem;
}

.success-box {
  margin-top: 1.5rem;
  padding: 1.6rem;
  background: #e8f5e9;
  border-radius: 12px;
}

/* Back button */
.back-btn {
  position: absolute;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: none;
  color: #5d6e66;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin: 0;
  left: 0.15rem;
  top: 1.35rem;
}

.form-header {
  text-align: center;
  position: relative;
  padding-top: 1.5rem;
  margin-bottom: 0;
}

/* Tooltip */
.info-tooltip {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: #666;
  color: white;
  border-radius: 50%;
  font-size: 11px;
  text-align: center;
  line-height: 16px;
  margin-left: 6px;
  cursor: help;
  position: relative;
}

.info-tooltip .tooltip {
  visibility: hidden;
  width: 284px;
  background: #333;
  color: white;
  text-align: left;
  border-radius: 6px;
  padding: 8px 10px;
  position: absolute;
  z-index: 10;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 0.85rem;
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
}

.info-tooltip:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
}

.guest-spinner {
  border-top-color: #275745;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transizioni slide */
.slide-left {
  transform: translateX(-100%);
}
.slide-right {
  transform: translateX(-50%);
  /* overflow-y: auto; */
  margin: 2rem 0;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.privacy-box {
  text-align: left;
}

.privacy-content {
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 1.25rem 0;
  color: #444;
}

.privacy-content p {
  margin-bottom: 0.9rem;
}
.privacy-p {
  text-align: center;
  margin-top: 2rem;
}
.privacy-accept {
  margin: 1.5rem 0 1rem;
  font-weight: 500;
}

.legal-checks {
  display: flex;
  flex-direction: column;
}

.privacy-accept label {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  cursor: pointer;
  line-height: 1.4;
}

.privacy-accept input {
  margin-top: 0.25rem;
}

.mt-3 {
  margin-top: 1.5rem;
}

/* Responsive */
@media (max-width: 992px) {
  .auth-container {
    flex-direction: column;
    height: auto;
    margin: 2rem 1rem;
  }
  .brand-panel,
  .form-panel {
    width: 80%;
    height: auto;
  }
  .brand-panel {
    border-radius: 20px 20px 0 0;
    padding: 3rem 2rem;
  }
  .form-panel {
    padding: 3rem 2rem;
  }
}

@media (max-width: 576px) {
  .type-selection {
    flex-direction: column;
  }
  .row {
    flex-direction: column;
  }
  .actions {
    flex-direction: column;
  }
  .btn-secondary {
    margin-left: 0;
    margin-top: 0.5rem;
  }
  .verification-box .actions {
    grid-template-columns: 1fr;
  }
  .verification-secondary-actions {
    width: 100%;
  }
}
.privacy-content.simplified {
  font-size: 0.98rem;
  line-height: 1.6;
  color: #444;
}

.privacy-content.simplified p {
  margin-bottom: 1rem;
}

.privacy-link-btn.prominent {
  font-size: 1.05rem;
  font-weight: 700;
  color: #2e7d32;
  text-decoration: underline;
  background: #e8f5e9;
  padding: 12px 24px;
  border-radius: 10px;
  border: 2px solid #a5d6a7;
  transition: all 0.2s;
}

.privacy-link-btn.prominent:hover {
  background: #d0e8d5;
  transform: translateY(-1px);
}

.legal-summary {
  margin-top: 1.5rem;
}

.type-card {
  padding: 2rem 1.5rem 1.6rem;
  border: 1px solid rgba(89, 125, 103, 0.18);
  border-radius: 22px;
  background: radial-gradient(
      circle at top,
      rgba(232, 245, 233, 0.95),
      rgba(255, 255, 255, 0.98) 60%
    ),
    #fafafa;
  box-shadow: 0 16px 40px rgba(44, 82, 58, 0.08);
}

.type-card:hover {
  box-shadow: 0 20px 45px rgba(38, 93, 55, 0.15);
}

.type-card.selected {
  background: linear-gradient(
      180deg,
      rgba(232, 245, 233, 0.98),
      rgba(245, 251, 246, 0.98)
    ),
    #e8f5e9;
  box-shadow: 0 22px 48px rgba(46, 125, 50, 0.18);
}

.type-card .card-title {
  margin-bottom: 0.7rem;
}

.type-card-icon {
  width: 3.35rem;
  height: 3.35rem;
  margin: 0 auto 1rem;
  border-radius: 18px;
  display: grid;
  place-items: center;
  font-size: 1.2rem;
  color: #1f5d3d;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.type-card-icon.new-studio {
  background: linear-gradient(135deg, #dff1e2, #f6fbf7);
}

.type-card-icon.existing-studio {
  background: linear-gradient(135deg, #e3efe8, #f9fcfa);
}

.registration-stage-card {
  margin: 0 0 0.9rem;
  padding: 0;
  border-radius: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.stage-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.62rem;
  border-radius: 999px;
  background: rgba(46, 125, 50, 0.08);
  color: #24563b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stage-badge.subtle {
  background: rgba(46, 125, 50, 0.09);
}

.highlight-label,
.review-item strong,
.review-label,
.legal-link-card strong,
.legal-link-card small,
.legal-link-eyebrow {
  display: block;
}

.highlight-label,
.review-label,
.legal-link-eyebrow {
  color: #6f8a79;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.form-highlight-card strong,
.review-item strong,
.legal-link-card strong {
  color: #224d36;
}

.form-highlight-card small,
.legal-link-card small {
  margin-top: 0.35rem;
  color: #6b7c72;
  line-height: 1.45;
  font-size: 0.88rem;
}

.summary-box-polished {
  width: 29rem;
  margin-left: -3rem;
  border: 1px solid rgba(80, 128, 93, 0.2);
  border-radius: 26px;
  padding: 2rem 1.7rem 1.6rem;
  background: linear-gradient(
      180deg,
      rgba(246, 252, 247, 0.98),
      rgba(238, 247, 240, 0.98)
    ),
    #f8fff9;
  box-shadow: 0 24px 56px rgba(34, 88, 55, 0.12);
}

.legal-consent-box {
  width: 38rem;
  margin-left: -7rem;
}

.final-review-box {
  width: min(42rem, calc(100vw - 3rem));
  margin-left: -6.5rem;
}

.summary-topline {
  margin-bottom: 0.85rem;
  color: #6e8c77;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
  margin: 1.1rem 0 1.35rem;
  text-align: left;
}

.review-item {
  padding: 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(123, 158, 132, 0.18);
  min-width: 0;
}

.review-item strong {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.review-item-wide {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem;
}

.review-item-wide .review-label,
.review-item-wide strong {
  margin: 0;
}

.review-item-wide strong {
  text-align: right;
}

.legal-panel {
  margin: 1.2rem 0 0.4rem;
  padding: 1.15rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(124, 159, 131, 0.18);
}

.legal-panel-head h4 {
  margin: 0.7rem 0 0.45rem;
  color: #1f5137;
  font-size: 1.12rem;
}

.legal-panel-head p {
  margin: 0;
  color: #66786e;
  line-height: 1.55;
}

.legal-links-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 1.05rem;
}

.privacy-accept label {
  padding: 0.95rem 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(119, 156, 127, 0.16);
}

.privacy-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
  margin: 1.1rem 0 0.3rem;
}

.privacy-summary-card {
  padding: 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(122, 160, 131, 0.16);
}

.privacy-summary-card strong {
  display: block;
  margin-bottom: 0.45rem;
  color: #224d36;
}

.privacy-summary-card p {
  margin: 0;
  color: #687a70;
}

.privacy-link-btn.prominent.legal-link-card {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: auto;
  min-width: 21rem;
  max-width: 100%;
  box-sizing: border-box;
  min-height: 0;
  padding: 0.72rem 0.85rem !important;
  border-radius: 16px;
  text-decoration: none;
  box-shadow: 0 12px 28px rgba(46, 125, 50, 0.08);
}

.privacy-link-btn.prominent.legal-link-card::before {
  content: "PDF";
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #1f6a45, #2e7d32);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  flex: 0 0 auto;
}

.polished-success-box {
  border-radius: 18px;
  border: 1px solid rgba(115, 159, 126, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.registration-success-panel {
  margin-top: 1.65rem;
  padding: 1.75rem 1.9rem 1.9rem;
}

.registration-success-panel h4 {
  margin: 0 0 0.65rem;
  color: #164f2b;
  font-size: 1.08rem;
  font-weight: 800;
  line-height: 1.35;
}

.registration-success-panel p {
  margin: 0 auto 1.35rem;
  max-width: 28rem;
  color: #244436;
  line-height: 1.45;
}

.registration-success-panel .btn-primary {
  margin-top: 0;
}

@media (max-width: 992px) {
  .summary-box-polished {
    width: 100%;
    margin-left: 0;
  }

  .legal-links-grid,
  .privacy-summary-grid,
  .review-grid,
  .form-highlight-row {
    grid-template-columns: 1fr;
  }

  .review-item-wide {
    display: block;
  }

  .review-item-wide strong {
    margin-top: 0.28rem;
    text-align: left;
  }
}

@media (max-width: 576px) {
  .registration-stage-card,
  .legal-panel,
  .summary-box-polished {
    padding: 1.2rem;
  }

  .privacy-link-btn.prominent.legal-link-card {
    width: 100%;
    min-width: 0;
    row-gap: 0.2rem;
  }
}

/* Restyling accesso e registrazione */
.centered-form .auth-form {
  width: 100%;
}

.auth-form h2 {
  margin: 0 0 0.55rem;
  color: #1b5e20;
  font-family: "Segoe UI", sans-serif;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: normal;
  line-height: normal;
}

.form-subtitle {
  margin: 0 0 2.25rem;
  color: #6d7772;
  font-size: 1.05rem;
}

.input-group {
  min-width: 0;
  margin-bottom: 1rem;
}

label {
  margin-bottom: 0.55rem;
  color: #24342e;
  font-size: 0.95rem;
  font-weight: 700;
}

.input-field {
  width: 100%;
  min-height: 54px;
  box-sizing: border-box;
  padding: 0.85rem 1rem;
  border: 1px solid #d6ded9;
  border-radius: 11px;
  background: #fff;
  color: #1f2d28;
  font: inherit;
  font-size: 1rem;
  box-shadow: inset 0 1px 2px rgba(25, 55, 43, 0.025);
}

.input-field::placeholder {
  color: #949d99;
}

.input-field:focus {
  border-color: #399467;
  box-shadow: 0 0 0 4px rgba(45, 139, 92, 0.11);
}

.field-with-icon {
  position: relative;
}

.field-with-icon > i:first-child {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 1rem;
  color: #78847e;
  font-size: 1rem;
  pointer-events: none;
  transform: translateY(-50%);
}

.input-field.has-leading-icon {
  padding-left: 3rem;
}

.password-wrapper .input-field {
  padding-right: 3rem;
}

.password-label-row {
  width: 100%;
}

.eye-icon {
  right: 1rem;
  color: #87918c;
}

.btn-primary,
.btn-guest,
.btn-secondary {
  min-height: 52px;
  box-sizing: border-box;
  border-radius: 11px;
  font-family: inherit;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  padding: 0.85rem 1.25rem;
  border: 0;
  background: linear-gradient(105deg, #2d9346 0%, #27863f 100%);
  box-shadow: 0 9px 20px rgba(35, 126, 63, 0.16);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(105deg, #267f3d 0%, #207537 100%);
  box-shadow: 0 12px 25px rgba(30, 108, 53, 0.22);
}

.btn-guest {
  background: #fbfdfc;
  border-color: #cadbd2;
  color: #17613f;
}

.switch-text {
  margin: 1.65rem 0 0;
  color: #737c78;
}

.link-btn,
.forgot-password-link {
  color: #19723b;
}

.form-header {
  padding-top: 0;
}

.form-header.choice-header {
  text-align: center;
}

.form-header.detail-header {
  padding-top: 2.2rem;
  text-align: left;
}

.auth-form-registration h2,
.auth-form-registration .title-large,
.auth-form-registration .title-small {
  margin: 0 !important;
  color: #1b5e20;
  font-family: "Segoe UI", sans-serif;
  font-size: 2rem !important;
  font-weight: 700;
  letter-spacing: normal;
  line-height: normal;
}

.auth-form-registration .detail-header .title-small {
  font-size: 1.7rem !important;
}

.registration-subtitle {
  margin: 1rem 0 0;
  color: #60766b;
  font-size: 1.22rem;
}

.registration-choice-form .auth-form-registration {
  justify-content: center;
}

.type-selection {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  width: 100%;
  max-width: none;
  margin: 2.4rem auto 2rem;
  padding: 0;
}

.type-card {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 285px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 2rem 2rem 3.2rem;
  border: 1px solid #d3e3da;
  border-radius: 18px;
  background: linear-gradient(145deg, #ffffff 10%, #f4faf6 100%);
  color: #173d31;
  font: inherit;
  box-shadow: 0 10px 26px rgba(33, 80, 60, 0.055);
  appearance: none;
}

.type-card:hover {
  border-color: #69aa83;
  background: linear-gradient(145deg, #ffffff 5%, #edf8f1 100%);
  box-shadow: 0 18px 38px rgba(32, 91, 66, 0.13);
  transform: translateY(-4px);
}

.type-card:focus-visible {
  outline: 3px solid rgba(45, 139, 92, 0.22);
  outline-offset: 3px;
}

.type-card-icon {
  width: 74px;
  height: 74px;
  margin: 0 auto 1.15rem;
  border-radius: 50%;
  background: #e7f5eb !important;
  color: #074b38;
  font-size: 1.9rem;
}

.type-card .card-title {
  margin: 0 0 0.65rem;
  color: #093e31;
  font-size: 1.45rem;
  font-weight: 800;
}

.type-card p {
  max-width: 15rem;
  margin: 0;
  color: #69736f;
  font-size: 1rem;
  line-height: 1.45;
}

.type-card-arrow {
  position: absolute;
  right: 1.15rem;
  bottom: 1.1rem;
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 50%;
  background: #dff0e5;
  color: #0f573e;
}

.registration-choice-form .switch-text {
  margin-top: 0.2rem;
  padding-top: 1.7rem;
  border-top: 1px solid #e2e8e4;
}

.back-btn {
  top: 0;
  left: 0;
  color: #53655d;
  font-size: 1rem;
  font-weight: 600;
}

.registration-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 1.35rem;
  width: 100%;
  margin-top: 1.25rem;
  text-align: left;
  transform: none;
}

.registration-form > .registration-stage-card,
.registration-form > .alert,
.registration-form > .row,
.registration-form > .full-width-field,
.registration-form > .btn-primary {
  grid-column: 1 / -1;
}

.registration-form .row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column: 1 / -1;
  gap: 1.35rem;
}

.registration-form .registration-location-row {
  grid-template-columns: minmax(7rem, 0.7fr) minmax(0, 1.6fr) minmax(
      6rem,
      0.6fr
    );
}

.uppercase-input,
.fiscal-code-input {
  text-transform: uppercase;
}

.registration-form .row .input-group {
  min-width: 0;
}

.registration-stage-card {
  margin-bottom: 1.2rem;
}

.stage-badge,
.summary-topline {
  color: #0e5d41;
  background: #e5f3e9;
}

.summary-topline {
  display: inline-flex;
  padding: 0.34rem 0.72rem;
  border-radius: 999px;
}

.summary-box,
.summary-box-polished,
.legal-consent-box,
.final-review-box {
  width: 100%;
  max-width: 760px;
  box-sizing: border-box;
  margin: 1.25rem auto 0;
}

.summary-box-polished {
  padding: clamp(1.35rem, 2.5vw, 2rem);
  border-radius: 20px;
  box-shadow: 0 18px 45px rgba(34, 88, 55, 0.09);
}

/* Primo passaggio compatto, con scorrimento naturale solo quando necessario. */
.form-wrapper.registration-first-step {
  padding-top: 1rem;
  padding-bottom: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scroll-padding-block: 0.75rem;
}

.registration-first-step .auth-form-registration {
  min-height: 100%;
  justify-content: flex-start;
}

.registration-first-step .back-btn {
  top: 0.6rem;
}

.registration-first-step .form-header.detail-header {
  padding-top: 1.35rem;
}

.registration-first-step .registration-form {
  margin-top: 0.9rem;
  column-gap: 1rem;
}

.registration-first-step .registration-stage-card {
  margin-bottom: 0.45rem;
}

.registration-first-step .input-group {
  margin-bottom: 0.45rem;
}

.registration-first-step label {
  margin-bottom: 0.3rem;
}

.registration-first-step .input-field {
  min-height: 44px;
  padding-block: 0.55rem;
}

.registration-first-step .registration-form .row {
  gap: 1rem;
}

.registration-first-step .btn-primary {
  position: static;
  min-height: 46px;
  margin-top: 0.75rem;
  padding-block: 0.65rem;
}

.registration-first-step .switch-text {
  margin-top: 0.65rem;
  font-size: 0.92rem;
}

.registration-first-step .scroll-spacer {
  display: none;
}

.registration-stage-card h3,
.registration-stage-card p {
  margin: 0;
}

.registration-stage-card h3 {
  margin-top: 0.6rem;
  color: #173f31;
  font-size: 1.08rem;
  line-height: 1.2;
}

.registration-stage-card p {
  margin-top: 0.2rem;
  color: #687a70;
  font-size: 0.84rem;
  line-height: 1.35;
}

.new-studio-registration-step .auth-form-registration,
.existing-studio-registration-step .auth-form-registration {
  justify-content: center;
  justify-content: safe center;
}

.new-studio-registration-step .registration-stage-card,
.existing-studio-registration-step .registration-stage-card {
  margin-bottom: 0.75rem;
}

.new-studio-registration-step .input-group,
.existing-studio-registration-step .input-group {
  margin-bottom: 0.72rem;
}

.new-studio-registration-step label,
.existing-studio-registration-step label {
  margin-bottom: 0.38rem;
}

.new-studio-registration-step .input-field,
.existing-studio-registration-step .input-field {
  min-height: 48px;
  padding-block: 0.65rem;
}

.new-studio-registration-step .registration-form .row,
.existing-studio-registration-step .registration-form .row {
  gap: 1.15rem;
}

.new-studio-registration-step .registration-form .btn-primary,
.existing-studio-registration-step .registration-form .btn-primary {
  min-height: 50px;
  margin-top: 1rem;
  padding-block: 0.75rem;
}

.new-studio-registration-step .switch-text,
.existing-studio-registration-step .switch-text {
  margin-top: 0.9rem;
}

.scrollable-form {
  scrollbar-color: rgba(66, 105, 87, 0.42) transparent;
  scrollbar-width: thin;
}

.scrollable-form::-webkit-scrollbar {
  width: 6px;
}

.scrollable-form::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-form::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(66, 105, 87, 0.38);
}

@media (min-width: 901px) and (min-height: 700px) {
  .form-wrapper.registration-choice-form {
    overflow-y: hidden;
  }
}

@media (max-width: 992px) {
  .auth-container {
    margin: 0;
  }

  .brand-panel,
  .form-panel {
    width: auto;
    height: auto;
  }

  .brand-panel {
    padding: clamp(2rem, 4vw, 3rem);
    border-radius: 0;
  }

  .form-panel {
    padding: 0;
  }
}

@media (max-width: 700px) {
  .registration-form .registration-location-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .auth-wrapper {
    align-items: flex-start;
    padding: 1rem;
  }

  .maincontainer {
    width: min(100%, 720px);
    height: auto;
    min-height: 0;
    overflow: visible;
  }

  .auth-container,
  .auth-container.registration-active {
    display: block;
    overflow: hidden;
  }

  .brand-panel {
    min-height: 300px;
    background-position: center 58%;
  }

  .brand-content {
    max-width: 420px;
  }

  .logo {
    width: 90px;
    margin-bottom: 0.65rem;
  }

  h1 {
    font-size: 2.55rem;
  }

  .tagline {
    margin-bottom: 1.4rem;
    font-size: 1.08rem;
  }

  .home-link {
    padding: 9px 18px;
  }

  .form-panel {
    min-height: 560px;
    padding: 3rem 2rem;
  }

  .form-wrapper,
  .scrollable-form {
    width: 100%;
    height: auto;
    max-height: none;
    padding: 0;
    overflow: visible;
  }

  .auth-form-registration {
    min-height: 0;
  }
}

@media (max-width: 640px) {
  .auth-wrapper {
    padding: 0;
  }

  .maincontainer,
  .auth-container {
    width: 100%;
    border-radius: 0;
  }

  .brand-panel {
    min-height: 230px;
  }

  .brand-panel h1 {
    font-size: 2.15rem;
  }

  .tagline {
    margin-bottom: 1rem;
  }

  .form-panel {
    min-height: 0;
    padding: 2.25rem 1.2rem 3rem;
  }

  .type-selection,
  .registration-form,
  .registration-form .row {
    grid-template-columns: 1fr;
  }

  .registration-form > *,
  .registration-form .row {
    grid-column: 1;
  }

  .type-card {
    min-height: 235px;
  }

  .auth-form h2,
  .auth-form-registration h2,
  .auth-form-registration .title-large,
  .auth-form-registration .title-small {
    font-size: 1.8rem !important;
  }

  .form-header.detail-header {
    padding-top: 2.5rem;
  }
}
</style>
